/**
 * Every notification code of §11.5, with first-draft Hungarian copy.
 *
 * §19.5 step 1: "Build the emails with first-draft Hungarian copy (magázó,
 * the conventions in §14): subject, content blocks and data fields per code,
 * on one shared layout … No email waits for polished copy." Step 3 is Barna's
 * copy review before launch, part of go-live gate 6 (§19.7). So the wording
 * below is deliberately first-draft and the structure is the deliverable:
 * every code has a subject, a preheader and the blocks §11.5 and §9.6 name.
 *
 * Three rules hold everywhere in this file.
 *
 *  1. **Magázó**, neutral register, first person plural for the company, "a
 *     Planteen" and never "a The Planteen", *100% növényi* (run 11 item 6c,
 *     run 12 item 7; it was *növényi alapú*), *étkű*, REAL
 *     School never shortened (§14).
 *  2. **Nothing is invented.** The fee ladder is §11.8's, the answer promise
 *     §11.4's, the refund window §9.8's, the gluten-free wording §14's. Where
 *     rev7 leaves the text to another document — the ÁSZF and the privacy
 *     notice are B14, the handover point and consumption window B08 — the
 *     email says the document is coming rather than inventing its content.
 *  3. **A customer email carries no machinery.** No production unit, no
 *     minimum batch, no margin, no capacity figure, no review reason, no
 *     price per dish (§5.11, docs/overnight_constraints.md §1 and §3).
 */

import { date, dateSuffixable, dateTime, ft, km, n, onDay, pct, qty, time, vatLabel } from './format.ts'
import { legalLinks, termsAttachedNote } from './legalLinks.ts'
import type { Block, Email } from './layout.ts'
import {
  allergenTextLong,
  dishNameCell,
  emailLang,
  eventLine,
  fulfilmentWhen,
  GLUTEN_FREE_NOTICE,
  lookalikeNote,
  mealNoteOf,
  osszesites,
  withMealNote,
} from './osszesites.ts'
import {
  docAllergenCaveat,
  docBands,
  docChanges,
  docDelivery,
  docDietary,
  docHeader,
  docLines,
  docTotals,
  DOCUMENT_MISSING,
  forecastBlocks,
  kitchenDocument,
  productionBlocks,
  type KitchenForecastDocument,
  type KitchenOrderDocument,
  type KitchenProductionDocument,
} from './kitchenDoc.ts'
import { CUSTOMER_EN } from './english.ts'
import { cardLinkOnDijbekero, FEE_TERM_KEYS, type EmailPayload, type FeeTermKey, type InvoicePayload, type OrderPayload } from './payload.ts'

export type Template = (payload: EmailPayload) => Email

/** §11.5's own description of each code, for the preview page and the
 *  coverage matrix. The database holds the same strings in English on
 *  `public.notification_codes`; this is what a Hungarian reader sees. */
export const CODE_TITLE: Record<string, string> = {
  C1: 'Visszaigazolva, felülvizsgálat nélkül',
  C2: 'Felülvizsgálatra került',
  C3: 'Felülvizsgálati emlékeztető',
  C4: 'Felülvizsgálati eszkaláció',
  C5: 'Új érdeklődés',
  C7: 'Fizetési határidő lejárt',
  C10: 'Napi véglegesítési összesítő',
  C11: 'Kapacitásküszöb',
  C12: 'Katalógusváltozás nyitott rendelést érint',
  C13: 'Módosítás érvénybe lépett',
  C14: 'Átadás nincs rögzítve',
  C15: 'Lezárás hiányos',
  C16: 'Planteen-javaslatra nincs válasz',
  C17: 'Visszahívás kérés',
  A1: 'Levélküldés ötször sikertelen',
  A2: 'Jogosultság-szinkron elutasítva',
  A3: 'Allergénadat újrahitelesítés nélkül változott',
  A4: 'SimplePay-értesítés váratlan státusszal',
  F1: 'Átutalási határidő fizetés nélkül telt le',
  F2: 'Kártyafoglalás vagy -terhelés sikertelen',
  F4: 'Visszatérítendő vagy mozgatandó összeg',
  F5: 'A szamlazz.hu Agent ötször sikertelen',
  F6: 'Az átutalt összeg eltér',
  F7: 'Lejárt visszatérítés',
  F8: 'Végszámla visszatartva',
  F9: 'Dokumentum PDF nélkül',
  F10: 'Előlegszámla ellenőrzése sikertelen',
  K1: 'Gyártási rendelés (véglegesítéskor)',
  K2: 'Gyártási rendelés (rövid határidő vagy késői rendelés)',
  K3: 'Módosítás véglegesítés után',
  K4: 'Lemondás véglegesítés után',
  K5: 'Gyártási emlékeztető',
  K6: 'Csomagolási lista és címkék',
  K7: 'Előrejelzés és konyhai naptár',
  K8: 'Étrendi megjegyzés jóváhagyásra',
  K9: 'Katalógusváltozás nyitott rendelést érint (konyha)',
  U1: 'Felülvizsgálat alatt',
  U2: 'Díjbekérő és átutalási adatok',
  U3: 'Fizetési emlékeztető',
  U4: 'Javaslatunk a rendelés módosítására',
  U5: 'Visszaigazolás és számla',
  U6: 'Elutasítva',
  U7: 'Fizetési határidő lejárt',
  U10: 'Módosítás érvénybe lépett',
  U11: 'Lemondás érvénybe lépett',
  U12: 'Emlékeztető az átadás előtti munkanapon',
  U13: 'Kérdőív és RevoBox',
  U14: 'Pontosított allergénlista',
  U15: 'Módosítási kérés megérkezett',
  U16: 'A rendelést a Planteen lemondta',
  U17: 'Visszahívási kérés rögzítve',
  U18: 'A visszahívási kérés lejárt',
  U19: 'Fizetési link az áremelkedéshez',
  U20: 'A módosítás nem lépett érvénybe',
  // `build/FIXES.md` item 79 (F8). **DRAFT**, and the mark is here rather
  // than in the copy: CODE_TITLE is the template list Barna and Anikó read,
  // and nothing a customer receives says DRAFT.
  U21: 'Köszönjük megrendelését! – visszaigazolásra vár (DRAFT)',
  U22: 'Visszaigazolt ajánlat és fizetési link (DRAFT)',
}

/* ------------------------------------------------------------------ *
 * Shared pieces
 * ------------------------------------------------------------------ */

const ORDER_MISSING =
  'Ehhez az értesítéshez nem tartozik rendelés. Ez hiba: a kódot rendeléssel együtt kell kiváltani.'

/** A template that needs an order and did not get one says so in the body
 *  rather than throwing. An outbox row that failed to render is a message
 *  nobody receives; one that renders a visible gap is one somebody fixes. */
function withOrder(payload: EmailPayload, build: (order: OrderPayload) => Email): Email {
  if (payload.order === null) {
    return {
      subject: `[${payload.code}] hiányzó rendelés`,
      preheader: ORDER_MISSING,
      blocks: [{ kind: 'band', text: ORDER_MISSING, tone: 'stop' }],
    }
  }
  return build(payload.order)
}

const greeting = (order: OrderPayload): Block => ({
  kind: 'paragraph',
  text: order.customer_name ? `Kedves ${order.customer_name}!` : 'Kedves Vendégünk!',
})

const signOff: Block = {
  kind: 'paragraph',
  text: 'Üdvözlettel, a Planteen csapata',
}

/** The short event recap an email uses when it does not carry the whole
 *  Összesítés: who, when, and which order it is about. */
const recap = (order: OrderPayload): Block => ({
  kind: 'facts',
  rows: [
    ['Rendelésazonosító', order.ref],
    ['Rendezvény', eventLine(order)],
  ],
})

/** The header every internal, finance and kitchen email starts with. Internal
 *  readers need the reference, the state and the date before anything else. */
function internalHeader(payload: EmailPayload, order: OrderPayload): Block {
  const rows: [string, string][] = [
    ['Rendelés', order.ref],
    ['Verzió', String(order.version)],
    ['Állapot', statusLabel(order)],
    ['Sáv', LANE_LABEL[order.lane] ?? order.lane],
    ['Rendezvény', eventLine(order)],
    [
      'Teljesítés',
      order.fulfilment === 'pickup'
        ? 'Személyes átvétel'
        : `Kiszállítás${order.zone ? ` · ${order.zone} zóna` : ''}${km(order.distance_km) === '' ? '' : ` · ${km(order.distance_km)}`}${order.postcode ? ` · ${order.postcode}` : ''}`,
    ],
    ['Vevő', [order.customer_company, order.customer_name, order.customer_email].filter(Boolean).join(' · ')],
  ]
  if (payload.internal?.customer_phone) rows.push(['Telefon', payload.internal.customer_phone])
  return { kind: 'facts', rows }
}

const LANE_LABEL: Record<string, string> = {
  card: 'kártya',
  transfer: 'átutalás',
  short_notice: 'rövid határidő',
}

/**
 * §11.1's status table, internal column. Every one of these enum values is an
 * identifier, and the first draft interpolated them straight into Hungarian
 * copy: *Állapot: confirmed* reached catering and finance, and a backticked
 * `paid` reached an inbox preview line. §11.1 fixes the Hungarian for each,
 * and this map is that column.
 *
 * The customer column of §11.1 has no map here because **no customer email
 * prints a status word**: each one says in prose what has happened, in the
 * register §11.1 sets — U1 says *a fizetés rendben van* for `paid`, U2 says
 * the order is confirmed on receipt rather than calling it *végleges*, which
 * §11.1 reserves for `frozen`. A map would invite a later stage to print a
 * state name at a customer instead of a sentence.
 *
 * `statusLabel` falls back to the raw value rather than hiding it: a status
 * nobody has labelled is a defect to see, not one to swallow.
 */
const STATUS_INTERNAL: Record<string, string> = {
  submitted: 'beérkezett',
  awaiting_payment: 'fizetésre vár',
  paid: 'fizetve',
  in_review: 'jóváhagyásra vár',
  confirmed: 'visszaigazolva',
  frozen: 'véglegesítve',
  delivered: 'átadva',
  closed: 'lezárva',
  cancelled: 'törölve',
}

/** §11.1's six mandatory cancellation reasons. */
const STATUS_REASON_LABEL: Record<string, string> = {
  abandoned: 'félbehagyott fizetés',
  payment_expired: 'fizetési határidő lejárt',
  declined: 'elutasítva',
  customer_cancelled: 'vevői lemondás',
  planteen_cancelled: 'Planteen általi lemondás',
  admin_cancelled: 'adminisztratív törlés',
}

const statusLabel = (order: OrderPayload): string => {
  const state = STATUS_INTERNAL[order.status] ?? order.status
  if (!order.status_reason) return state
  return `${state} · ${STATUS_REASON_LABEL[order.status_reason] ?? order.status_reason}`
}

/** §9.9's six payment kinds and the five statuses of `payments_status_known`. */
const PAYMENT_KIND_LABEL: Record<string, string> = {
  reservation: 'foglalás',
  capture: 'terhelés',
  release: 'feloldás',
  transfer_received: 'átutalás beérkezett',
  topup: 'ráfizetés',
  refund: 'visszatérítés',
}

const PAYMENT_STATUS_LABEL: Record<string, string> = {
  started: 'elindítva',
  succeeded: 'sikeres',
  failed: 'sikertelen',
  cancelled: 'megszakítva',
  done: 'kész',
}

/** §9.9's invoice kinds. `invoiceBlock` had this map privately; the invoice
 *  table needs the same words, so it lives here and both read it. The two of
 *  build/FIXES.md item 88 are named as the accountant's answers and the brief
 *  name them -- the documents' own Hungarian names, not page copy. */
const INVOICE_KIND_LABEL: Record<
  string,
  'Díjbekérő' | 'Számla' | 'Előlegszámla' | 'Végszámla' | 'Sztornó számla' | 'Díjszámla'
> = {
  dijbekero: 'Díjbekérő',
  szamla: 'Számla',
  elolegszamla: 'Előlegszámla',
  vegszamla: 'Végszámla',
  storno: 'Sztornó számla',
  fee: 'Díjszámla',
}

/** §9.9 `invoices.status`. */
const INVOICE_STATUS_LABEL: Record<string, string> = {
  queued: 'sorban áll',
  issued: 'kiállítva',
  failed: 'sikertelen',
  held: 'visszatartva',
}

/** §9.9 `pending_changes.status`. */
const PENDING_CHANGE_STATUS_LABEL: Record<string, string> = {
  awaiting_payment: 'fizetésre vár',
  applied: 'érvénybe lépett',
  lapsed: 'lejárt',
}

/**
 * §11.8's six Planteen-initiated reason codes, as the **customer** reads them.
 *
 * Two rules shape this map. The codes are identifiers and U4 printed one
 * verbatim — *Az ok: dish_unavailable* — in a Hungarian customer email. And
 * `capacity` must not reach the customer at all: a capacity figure or reason
 * is internal machinery (`docs/overnight_constraints.md` §1, handover §5.11),
 * so it resolves to the neutral *Egyéb ok* like any code nobody has worded
 * yet. Staff see the real reason in the console; the customer sees why their
 * order is changing, not how full the kitchen is.
 */
const CHANGE_REASON_CUSTOMER: Record<string, string> = {
  dish_unavailable: 'Az egyik fogás nem elérhető erre a napra',
  allergen_correction: 'Allergénadat pontosítása',
  delivery: 'Kiszállítási ok',
  force_majeure: 'Elháríthatatlan ok',
  other: 'Egyéb ok',
}

const changeReasonCustomer = (reason: string | null | undefined): string | null =>
  reason ? (CHANGE_REASON_CUSTOMER[reason] ?? 'Egyéb ok') : null

/** §11.4's reason codes, as staff read them. Internal only — §5.11 keeps the
 *  reasons off every customer-facing surface. */
const REVIEW_REASON_LABEL: Record<string, string> = {
  value: 'értékhatár',
  covers: 'létszámhatár',
  new_customer_value: 'új vevő értékhatár felett',
  clash_date: 'ütköző dátum',
  zone_c: 'C zóna',
  dietary_note: 'étrendi megjegyzés',
  capacity_soft: 'kapacitás lágy küszöb',
}

/**
 * §5.9's enquiry reason codes, as catering reads them in C5.
 *
 * The same Hungarian as `supabase/functions/_shared/messages.ts`, which is
 * what the customer was shown when the form routed them here. `routed_by` and
 * the customer's own ticks both arrive in this list, so both are covered. An
 * unmapped code falls through raw, like every other label map in this file:
 * visible is better than swallowed.
 */
const ENQUIRY_REASON_LABEL: Record<string, string> = {
  /* `build/FIXES.md` item 72 (F1), Barna's ruling 1 of 8 October 2026: the
     Planteen provides no equipment without staff, so the three sub-options
     are gone and the reason says what the one question now asks. The code is
     unchanged -- `083_confirm_first` is what re-homes it under
     `public.confirmation_reason` as `staff_equipment`. */
  staff_or_equipment: 'helyszíni kiszolgálást kér személyzettel és eszközökkel',
  custom_menu: 'egyedi menüt kér',
  not_only_food: 'más étel is lesz a rendezvényen, a mennyiséget közösen beszéljük meg',
  above_contract_value: 'az érték a szerződéses értékhatár fölött van',
  short_notice_limit: 'rövid határidőre ennyi ételt csak egyeztetés után tudunk vállalni',
  short_notice_gluten_free: 'rövid határidőre gluténmentes választékot nem tudunk biztosítani',
  short_notice_review: 'rövid határidőn ellenőrzést igényel',
}

/**
 * §11.9's watched fields, as the kitchen and the signatory read them in C12
 * and K9. `catalogue_changes.field` is a **column name**, so without this the
 * alert reads *gluten_free_eligible*. Nothing writes the table yet —
 * `042_watcher_closeout` does — so the keys are the columns §11.9 names, and
 * anything else prints as words rather than as source.
 */
const CATALOGUE_FIELD_LABEL: Record<string, string> = {
  allergens: 'allergének',
  allergen_status: 'allergén-ellenőrzés állapota',
  allergen_test_marked: 'allergénadat tesztjelölése',
  gluten_free_eligible: 'gluténmentesség',
  name_hu: 'magyar név',
  name_en: 'angol név',
  description_hu: 'magyar leírás',
  description_en: 'angol leírás',
  net_price: 'nettó ár',
  withdrawn: 'kivezetett fogás',
}

const catalogueFieldLabel = (field: string): string => {
  const known = CATALOGUE_FIELD_LABEL[field]
  if (known) return known
  const words = String(field).replace(/_/g, ' ').trim()
  return words.charAt(0).toUpperCase() + words.slice(1)
}

const reviewReasons = (payload: EmailPayload): Block | null => {
  const reasons = payload.internal?.review_reasons ?? []
  if (reasons.length === 0) return null
  return {
    kind: 'list',
    items: reasons.map((reason) => REVIEW_REASON_LABEL[reason] ?? reason),
  }
}

/**
 * `public.confirmation_reason`'s seven, as catering reads them in C2.
 *
 * The same Hungarian the customer was shown on the page, shortened to the
 * noun a queue is skimmed by — the pattern `REVIEW_REASON_LABEL` and
 * `ENQUIRY_REASON_LABEL` already set. An unknown code prints as its code,
 * which is the one thing worse than a label but better than a blank.
 */
const CONFIRMATION_REASON_LABEL: Record<string, string> = {
  staff_equipment: 'helyszíni kiszolgálás személyzettel és eszközökkel',
  coffee: 'kávé, személyzettel és eszközökkel',
  dietary_note: 'allergia vagy étrendi igény',
  custom_menu: 'egyedi menü',
  other_food: 'más étel is lesz a rendezvényen',
  contract_value: 'a szerződéses értékhatár fölött',
  customer_comment: 'megjegyzés vagy különleges kérés',
  /* `build/FIXES.md` item 81 (P2): the six automatic reasons of §11.4, which
     C2 now carries as well. The customer never saw these, so there is no page
     sentence to shorten: each is the word `REVIEW_REASON_LABEL` already uses
     for the same rule, so C2 and the review queue read alike. This is a staff
     label and not page copy — item 81 shows the six to staff and to nobody
     else. */
  value: 'értékhatár',
  covers: 'létszámhatár',
  new_customer_value: 'új vevő értékhatár felett',
  clash_date: 'ütköző dátum',
  zone_c: 'C zóna',
  capacity_soft: 'kapacitás lágy küszöb',
  /* Run 9, item 4: over portion_cap or the day's guest limit; sent for
     confirmation instead of being refused. */
  capacity_over: 'napi kapacitás felett',
  /* Run 13a item 2 (DECIDED 20261012): a transfer recorded after the order
     expired; approve to confirm it, decline to refund it. */
  late_payment: 'késve érkezett fizetés',
}

const confirmationReasons = (payload: EmailPayload): Block => {
  const reasons = payload.internal?.confirmation_reasons ?? []
  if (reasons.length === 0) return { kind: 'note', text: 'Nincs rögzített ok.' }
  return {
    kind: 'list',
    items: reasons.map((reason) => CONFIRMATION_REASON_LABEL[reason] ?? reason),
  }
}

const consoleButton = (payload: EmailPayload, label = 'Megnyitás a konzolban'): Block | null =>
  payload.internal?.console_url ? { kind: 'button', label, href: payload.internal.console_url } : null

const keep = (blocks: (Block | null)[]): Block[] => blocks.filter((block): block is Block => block !== null)

/**
  * §11.4 *Answer promise*. Barna, 10 October 2026: the customer-facing
  * sentence is *Igyekszünk 24 órán belül válaszolni.* everywhere. The
  * scheduling logic behind the old 15:00/16:00 wording
  * (`confirmation_answer_due()`, the too-late-to-confirm block) is unchanged.
  */
const ANSWER_PROMISE = 'Igyekszünk 24 órán belül válaszolni.'

/** The same promise as an inbox line. A preheader is a fragment beside the
 *  subject, not a place to splice a whole sentence into. */
const ANSWER_PROMISE_SHORT = 'válasz 24 órán belül'

/** §9.8: *5 munkanapon belül*, from `refund_due_bd` (5, PROPOSED). The number
 *  travels in the payload so a change to the parameter changes the sentence. */
const refundSentence = (payload: EmailPayload): string => {
  const due = payload.payments.find((payment) => payment.kind === 'refund' && payment.due_at !== null)
  return due
    ? `A visszatérítést 5 munkanapon belül, ${dateSuffixable(due.due_at ?? '')}-ig indítjuk.`
    : 'A visszatérítést 5 munkanapon belül indítjuk.'
}

/** §9.7: "Every customer email carrying a fee shows the base, the percentage,
 *  the fee, what was paid and the balance." */
/**
 * The base a fee is quoted on, in the buyer's own words.
 *
 * **Barna's ruling 8 of 5 October 2026** (`build/FIXES.md` item 44): a
 * consumer's fee is a percentage of the order's gross total, a business
 * buyer's a percentage of the net price with VAT added on the invoice as on
 * every other line. So the two read different words for the same money, and
 * each must read only its own: a consumer who is told *a nettó ár 30%-a* has
 * been quoted on a figure the page never showed them.
 *
 * `base_kind` comes from `public.fee_base_kind()`, which is the one place the
 * rule lives, so the ladder, the fee block and the arithmetic cannot read it
 * three ways.
 */
const FEE_BASE_WORD: Record<string, { label: string; of: string; genitive: string; mod: string }> = {
  gross: {
    label: 'Díjalap (a rendelés végösszege)',
    of: 'a rendelés végösszege',
    genitive: 'a rendelés végösszegének',
    /* Run 16 item 3(d) (DECIDED 20261015, Barna): the modification fee's base
       is the changed lines, not the whole order. Only the modification's base
       phrase changes; the cancellation keeps `genitive`. */
    mod: 'a módosított tételek értékének',
  },
  net: {
    label: 'Díjalap (nettó)',
    of: 'a nettó ár',
    genitive: 'a nettó ár',
    mod: 'a módosított tételek nettó értékének',
  },
}

const feeBase = (kind: string | undefined): { label: string; of: string; genitive: string; mod: string } =>
  FEE_BASE_WORD[kind ?? 'gross'] ?? FEE_BASE_WORD['gross']!

const feeBlock = (payload: EmailPayload): Block | null => {
  const fee = payload.fee
  if (fee === null) return null
  const base = feeBase(fee.base_kind)
  // §9.7 asks for the base, the percentage, the fee, what was paid and the
  // balance. Which base, and therefore which fee amount, is ruling 8's: a
  // consumer reads the gross figures throughout, a business buyer the net,
  // and the balance is of money actually received, which is gross either way.
  const rows: [string, string][] = [
    [base.label, ft(fee.base)],
    ['Díj mértéke', pct(fee.pct)],
    ['Díj', fee.base_kind === 'net' ? ft(fee.amount) : ft(fee.amount_gross)],
    ['Eddig fizetve', ft(fee.paid)],
    ['Egyenleg', ft(fee.balance)],
  ]
  if (fee.base_kind === 'net') rows.splice(3, 0, ['Díj bruttó', ft(fee.amount_gross)])
  if (fee.waived) rows.push(['Díj elengedve', fee.note ?? 'igen'])
  return { kind: 'facts', rows }
}

/**
 * §11.8's ladder, stated as it is written there. Nothing is inferred about
 * which rung this order is on: the fee block above says what was charged.
 *
 * The ladder's first boundary is the freeze point, and §11.8 states it as a
 * **time** — *E − 5 bd, 14:00* — never as a named event. The first draft
 * called it *a gyártászár*, a word that appears in no document of this
 * project: it is a production term, it means "production close", and
 * `docs/overnight_constraints.md` §1 keeps production machinery off every
 * customer surface. §11.1 reserves *végleges* for the state itself. So the
 * customer reads the deadline, which is the thing that concerns them.
 */
const feeLadder = (payload: EmailPayload): string | null => {
  // Ruling 8: the rung names the base this buyer is quoted on. The buyer type
  // is read from the fee when there is one and from the order otherwise,
  // because U15 carries the ladder before any fee has been worked out.
  const kind = payload.fee?.base_kind ?? (payload.order?.is_business === true ? 'net' : 'gross')
  const { genitive, mod: modBase } = feeBase(kind)
  // Run 11 item 6b: every figure is the register's (`payload.terms`). A
  // missing one leaves the ladder out rather than printing a number nobody set.
  const terms = payload.terms ?? {}
  const figure = (key: FeeTermKey): string | null => {
    const raw = (terms[key] ?? '').trim()
    if (raw === '') return null
    if (key === 'freeze_time') return time(raw) || null
    const value = Number(raw.replace(',', '.'))
    return Number.isFinite(value) ? n(value) : null
  }
  const [mod, cancel, within, windowH, noticeBd, freezeTime] = FEE_TERM_KEYS.map(figure)
  if ([mod, cancel, within, windowH, noticeBd, freezeTime].some((value) => value === null)) return null
  /* Run 16 item 3(d): the two fees no longer share a base, so the rung
     inside the fee window names each of them. The cancellation's wording is
     the one that was already here; only the modification's base phrase is
     new. */
  return (
    `Módosítási és lemondási díjaink: a rendezvény előtt ${noticeBd} munkanappal, ${freezeTime}-ig díjmentes; ` +
    `ettől az időponttól a rendezvény előtt ${windowH} óráig a módosítás ${modBase} ${mod}%-a, a lemondás ` +
    `${genitive} ${cancel}%-a; ${windowH} órán belül a módosítás ${modBase} ${within}%-a, a lemondás ` +
    `${genitive} ${within}%-a.`
  )
}

/** The ladder as a block of the given kind, or nothing when it cannot be told. */
const feeLadderBlock = (payload: EmailPayload, kind: 'paragraph' | 'note'): Block | null => {
  const text = feeLadder(payload)
  return text === null ? null : { kind, text }
}

/** §14 / B14: the ÁSZF and the privacy notice. Until 13 October 2026 this was
 *  a gap note; the legal pages brief (item 5) filled it with the two links,
 *  in the customer's language. */
const termsNote = (payload: EmailPayload): Block[] => legalLinks(payload, 'hu')

/**
 * **build/FIXES.md item 93: two sentences nobody has written yet.** Each slot
 * is for one sentence saying the invoice arrives separately, from
 * szamlazz.hu -- in U5 and in U13. The brief gives no wording ("build the slot,
 * leave it empty and hidden, and list it in the report for Anikó"), so both
 * are empty, and an empty slot renders nothing at all: no placeholder, no gap
 * marker, nothing a customer can see. Anikó's copy goes here, and nowhere
 * else needs to change.
 */
export const COPY_SLOTS: Readonly<Record<'U5_invoice_from_szamlazz' | 'U13_invoice_from_szamlazz', string>> = {
  U5_invoice_from_szamlazz: '',
  U13_invoice_from_szamlazz: '',
}

/** One slot as a block, or nothing while it is empty. */
export function copySlot(name: keyof typeof COPY_SLOTS): Block | null {
  const text = COPY_SLOTS[name].trim()
  return text === '' ? null : { kind: 'paragraph', text }
}

const invoiceBlock = (payload: EmailPayload, kind: InvoicePayload['kind']): Block | null => {
  const invoice = [...payload.invoices].reverse().find((candidate) => candidate.kind === kind)
  if (invoice === undefined) return null
  const label = INVOICE_KIND_LABEL[kind] ?? kind
  const rows: [string, string][] = [
    [`${label} sorszáma`, invoice.szamlazz_number ?? 'még készül'],
    ['Bruttó', ft(invoice.gross)],
  ]
  if (invoice.issued_at) rows.push(['Kiállítva', date(invoice.issued_at)])
  return { kind: 'facts', rows }
}

/**
 * Run 11 items 5 and 6a: the transfer payment deadline as the page names it —
 * *2026. 10. 15. csütörtök 09:07* — from `order.payment_deadline`, which
 * `public.transfer_payment_deadline` set (díjbekérő + payment_window_bd
 * business days, capped at the order deadline). Empty when there is none.
 */
const payBy = (order: OrderPayload): string => {
  const raw = String(order.payment_deadline ?? '')
  const day = date(raw, true)
  const clock = time(raw.slice(11))
  return day === '' ? '' : clock === '' ? day : `${day} ${clock}`
}

/** The page's transfer sentence, word for word (Step4Summary's PaymentLine). */
const finalWhenPaid = (order: OrderPayload): string =>
  payBy(order) === ''
    ? 'A rendelés akkor válik véglegessé, ha az összeg a fizetési határidőig beérkezik.'
    : `A rendelés akkor válik véglegessé, ha az összeg ${payBy(order)}-ig beérkezik.`

/** §9.5: the díjbekérő carries the bank details. Empty values leave the line
 *  off rather than printing a placeholder account number. */
const transferBlock = (payload: EmailPayload, order: OrderPayload): Block => {
  const rows: [string, string][] = []
  if (payload.site.bank.name) rows.push(['Bank', payload.site.bank.name])
  if (payload.site.bank.account) {
    rows.push(['Számlaszám', payload.site.bank.account])
  } else {
    rows.push(['Számlaszám', 'HIÁNYZÓ ADAT: a bankszámlaszám nincs beállítva (§9.5)'])
  }
  rows.push(['Összeg', ft(order.gross)])
  rows.push(['Közlemény', order.ref])
  if (order.payment_deadline) rows.push(['Fizetési határidő', payBy(order)])
  return { kind: 'facts', rows }
}

/** The address a customer writes to: the register's Reply-To, else the
 *  catering recipient. Never a literal in a template. */
export const contactAddress = (payload: EmailPayload): string =>
  (payload.site.reply_to ?? payload.site.catering_email ?? '').trim()

const publicLink = (order: OrderPayload, label = 'Rendelés megtekintése'): Block | null =>
  order.public_url ? { kind: 'button', label, href: order.public_url } : null

/* ------------------------------------------------------------------ *
 * `build/FIXES.md` item 79 (F8): the two DRAFT emails' own pieces
 * ------------------------------------------------------------------ */

/* Run 12 item B: the English sign-off and answer promise of item 79 moved to
   english.ts with the English emails. */

/** The confirmed offer's deadline — since run 12 item 5, `payment_window_bd`
 *  business days from the approval, capped. The instant is on the order, so the sentence cannot name a
 *  window the order is not actually on. */
const confirmPayDeadline = (order: OrderPayload, lang: 'hu' | 'en'): Block | null =>
  order.confirm_pay_deadline
    ? {
        kind: 'facts',
        rows: [
          [
            lang === 'en' ? 'Please pay by' : 'Fizetési határidő',
            dateTime(order.confirm_pay_deadline),
          ],
        ],
      }
    : null

/** *…and a link to decline.* The same token, with one parameter the return
 *  page reads; nothing is paid either way. */
const declineLink = (order: OrderPayload, lang: 'hu' | 'en'): Block | null =>
  order.public_url
    ? {
        kind: 'paragraph',
        text:
          lang === 'en'
            ? `If you would rather not go ahead, you can decline the offer here: ${order.public_url}?decline=1 — nothing is charged.`
            : `Ha mégsem kéri, itt tudja elutasítani az ajánlatot: ${order.public_url}?decline=1 – ebben az esetben semmit nem számítunk fel.`,
      }
    : null

/* ------------------------------------------------------------------ *
 * Customer emails (§11.5 Customer; Hungarian, magázó)
 * ------------------------------------------------------------------ */

const customer: Record<string, Template> = {
  // §9.6: "U1 (under review, with the answer promise)". §5.11: the page says
  // only that a colleague approves it; the reasons are not shown, and neither
  // are they here.
  U1: (payload) =>
    withOrder(payload, (order) => ({
      subject: `Megkaptuk a rendelését · ${order.ref}`,
      preheader: 'Kollégánk hagyja jóvá. Hamarosan válaszolunk.',
      blocks: [
        greeting(order),
        {
          kind: 'paragraph',
          text:
            'Köszönjük a rendelését. A fizetés rendben van. Ezt a rendelést kollégánk hagyja jóvá, ' +
            'ezért még nincs visszaigazolva.',
        },
        { kind: 'band', text: ANSWER_PROMISE, tone: 'neutral' },
        ...osszesites(order),
        ...keep([publicLink(order)]),
        ...termsNote(payload),
        signOff,
      ],
    })),

  // §9.6: U2 replaces U1 on the transfer lane and carries the díjbekérő and
  // the transfer details. §9.2: a díjbekérő is never labelled *számla*.
  U2: (payload) =>
    withOrder(payload, (order) => ({
      subject: `Díjbekérő a rendeléséhez · ${order.ref}`,
      preheader: `Fizetendő ${ft(order.gross)}, a közlemény ${order.ref}.`,
      blocks: keep([
        greeting(order),
        {
          kind: 'paragraph',
          // Run 11 item 6a: the page's sentence, with the same deadline.
          text:
            // Card payment change 3 (DECIDED 20261101): the díjbekérő carries
            // the card payment link while the route is dijbekero.
            (cardLinkOnDijbekero(payload)
              ? 'Köszönjük a rendelését. A díjbekérőt külön e-mailben kapja meg, a Számlázz.hu rendszerén keresztül, ' +
                'benne a bankkártyás fizetés linkjével és az utalási adatokkal. '
              : 'Köszönjük a rendelését. A díjbekérőt külön e-mailben kapja meg, a Számlázz.hu rendszerén keresztül. ') +
            `${finalWhenPaid(order)} A beérkezés után visszaigazoljuk.`,
        },
        { kind: 'heading', text: 'Átutalási adatok' },
        transferBlock(payload, order),
        invoiceBlock(payload, 'dijbekero'),
        { kind: 'note', text: 'A díjbekérő nem számla. Az előlegszámlát a fizetés beérkezése után állítjuk ki, és szintén külön e-mailben kapja meg.' },
        ...osszesites(order),
        publicLink(order),
        ...termsNote(payload),
        signOff,
      ]),
    })),

  // §9.6: "75 % of the payment window → U3". Run 11 item 5: the window is
  // `payment_window_bd` business days from the díjbekérő, and the reminder
  // goes at `payment_reminder_pct` of it (sweep_transfer_windows).
  U3: (payload) =>
    withOrder(payload, (order) => ({
      subject: `Emlékeztető: a rendelése fizetésre vár · ${order.ref}`,
      preheader: finalWhenPaid(order),
      blocks: keep([
        greeting(order),
        {
          kind: 'paragraph',
          // Run 11 item 6a: the page's sentence, with the same deadline.
          text:
            (cardLinkOnDijbekero(payload)
              ? 'A rendeléséhez tartozó fizetés még nem érkezett meg. A Számlázz.hu díjbekérő e-mailjében ' +
                `bankkártyás fizetési link és az utalási adatok is szerepelnek. ${finalWhenPaid(order)} `
              : `A rendeléséhez tartozó átutalás még nem érkezett meg. ${finalWhenPaid(order)} `) +
            'Ha addig nem érkezik meg, a rendelés és a hozzá tartozó időpont felszabadul.',
        },
        recap(order),
        { kind: 'heading', text: 'Átutalási adatok' },
        transferBlock(payload, order),
        publicLink(order),
        signOff,
      ]),
    })),

  // §9.6 *Amendment proposed at review*. The price effect and what happens on
  // acceptance differ by lane; the email states the new total and asks for an
  // answer, and the four outcomes are staff's to apply.
  U4: (payload) =>
    withOrder(payload, (order) => ({
      subject: `Javaslatunk a rendelés módosítására · ${order.ref}`,
      preheader: 'Egy részletet módosítanunk kell. Kérjük, jelezze vissza.',
      blocks: keep([
        greeting(order),
        {
          kind: 'paragraph',
          text:
            'A rendelését átnéztük, és egy részletén módosítanunk kell. A javaslatunk az alábbi ' +
            'összeállítás. Kérjük, válaszoljon erre a levélre, hogy elfogadja-e.',
        },
        changeReasonCustomer(payload.pending_change?.reason)
          ? { kind: 'band', text: `Az ok: ${changeReasonCustomer(payload.pending_change?.reason)}`, tone: 'warn' }
          : null,
        ...osszesites(order),
        payload.pending_change && payload.pending_change.amount_due !== 0
          ? {
              kind: 'facts',
              rows: [
                [
                  payload.pending_change.amount_due > 0 ? 'Fizetendő különbözet' : 'Visszajáró különbözet',
                  ft(Math.abs(payload.pending_change.amount_due)),
                ],
              ],
            }
          : null,
        {
          kind: 'note',
          text:
            'A módosítás a visszajelzéséig nem lép érvénybe. Ha nem fogadja el, a rendelést ' +
            'lemondjuk és a teljes összeget visszatérítjük.',
        },
        signOff,
      ]),
    })),

  // §9.6: "U5 with invoice (replaces U1)", and §11.5: "confirmation with the
  // invoice attached, where the contract forms, with the Összesítés as on the
  // page … dishes, allergen codes and names, gluten-free notice, fulfilment,
  // payment and the terms for this order's dates".
  U5: (payload) =>
    withOrder(payload, (order) => ({
      subject: `Visszaigazoltuk a rendelését · ${order.ref}`,
      preheader: `${date(order.event_date)} · ${n(order.guests)} vendég · ${ft(order.gross)}`,
      blocks: keep([
        greeting(order),
        {
          kind: 'band',
          text: 'A rendelését visszaigazoltuk. Ezzel a szerződés létrejött.',
          tone: 'good',
        },
        ...osszesites(order),
        { kind: 'heading', text: 'Fizetés és számla' },
        // Item 85: a new order's money is documented by an előlegszámla; an
        // order placed before run 8 still has its számla. Item 93: nothing
        // waits for either, so this is usually the sentence below -- whose
        // wording is unchanged and is listed for Anikó beside the slot.
        invoiceBlock(payload, 'elolegszamla') ?? invoiceBlock(payload, 'szamla') ?? {
          kind: 'note',
          text: 'Az előlegszámlát külön e-mailben kapja meg, a Számlázz.hu rendszerén keresztül.',
        },
        copySlot('U5_invoice_from_szamlazz'),
        { kind: 'heading', text: 'Módosítás és lemondás' },
        feeLadderBlock(payload, 'paragraph'),
        order.freeze_at
          ? {
              kind: 'facts',
              rows: [['Díjmentes módosítás határideje', dateTime(order.freeze_at)]],
            }
          : null,
        {
          kind: 'paragraph',
          // The address is the register's Reply-To (email_reply_to), not a
          // literal: the test project uses test.catering@planteen.hu
          // (DECIDED 20261012, Barna), production catering@planteen.hu.
          text:
            `Módosítást vagy lemondást erre a levélre válaszolva, ${contactAddress(payload) ? `a ${contactAddress(payload)} címen ` : ''}vagy ` +
            'telefonon, írásban megerősítve tud kérni. A kérés a beérkezésétől számít.',
        },
        publicLink(order),
        // Legal pages brief, item 5: the ÁSZF PDF of the order's version.
        termsAttachedNote(order, 'hu'),
        ...termsNote(payload),
        signOff,
      ]),
    })),

  // §9.6 Review, declined. On the transfer lane the money is already in, so
  // the email also carries the refund (F4 goes to Finance in the same breath).
  U6: (payload) =>
    withOrder(payload, (order) => ({
      subject: `Sajnáljuk: a rendelését nem tudjuk vállalni · ${order.ref}`,
      preheader: 'A rendelést nem tudjuk vállalni. A befizetett összeget visszatérítjük.',
      blocks: keep([
        greeting(order),
        {
          kind: 'paragraph',
          text:
            'Átnéztük a rendelését, és sajnáljuk, de erre az időpontra nem tudjuk vállalni. ' +
            'Semmilyen díjat nem számítunk fel.',
        },
        recap(order),
        // `build/FIXES.md` item 83 (P4): the card branch said the amount
        // reserved on the card had been released, and is deleted with nothing
        // in its place. The transfer lane's refund sentence is unchanged.
        order.lane === 'transfer' ? { kind: 'paragraph', text: refundSentence(payload) } : null,
        invoiceBlock(payload, 'storno'),
        {
          kind: 'paragraph',
          text: 'Ha más időpont szóba jöhet, írjon nekünk — szívesen keresünk megoldást.',
        },
        signOff,
      ]),
    })),

  /* ------------------------------------------------------------------ *
   * `build/FIXES.md` item 79 (F8) — the two DRAFT emails
   *
   * Barna's ruling 9 of 8 October 2026 gives the order a state rev7 has no
   * email for: priced, sent, and waiting for a colleague with **nothing
   * paid**. Item 79 asks for two new customer emails and makes them *the one
   * exception to the copy rule*: the build drafts them, HU and EN, and they
   * are **marked DRAFT in the template list** for Barna's and Anikó's review.
   *
   * Everything else here follows the existing templates: magázó, first person
   * plural for the company, *a Planteen*, the Összesítés block §11.5 asks
   * for, no review reason, no price per dish and no machinery. The answer
   * promise is §11.4's own sentence, reused rather than rewritten.
   *
   * Run 12 item B (DECIDED 20261012) allows English orders, and every
   * customer email has an English version in `english.ts`; the English of
   * these two is there too, unchanged from item 79's draft.
   * ------------------------------------------------------------------ */

  // Run 12 item B: the English is CUSTOMER_EN.U21 (english.ts), chosen by
  // buildEmail for an English order; this is the Hungarian.
  U21: (payload) =>
    withOrder(payload, (order) =>
      payload.lang === 'en'
        ? CUSTOMER_EN['U21']!(payload)
        : {
            /* **Run 19 §14 (Fede's review of 16 October, DECIDED
               20261016).** The subject, the body and the button are Fede's
               words, with the three PROPOSED corrections the run 19 plan
               names:

                 * the **final price** is back in the follow-up sentence.
                   Staff can change lines on review, so the total in this
                   email is not binding, and *a végleges árat és* is what
                   stops the customer reading it as fixed;
                 * the **answer promise** stays as the last sentence. The
                   spec requires it in this email and Fede's body leaves it
                   out;
                 * *Kérés* becomes *megrendelés* **in this email only**. The
                   confirm-first button on the page and the console's status
                   names are untouched and go to the B71 copy round.

               The brand in the header is still the text *Planteen*: §14 asks
               for the logo as a hosted image and there is no email-ready
               logo file in the repo. Q8 in the register. */
            subject: `Köszönjük megrendelését! · ${order.ref}`,
            preheader: 'Most nem kell fizetnie. Kollégánk átnézi.',
            blocks: keep([
              greeting(order),
              {
                kind: 'paragraph',
                text:
                  'Köszönjük megrendelését! A rendezvénnyel kapcsolatos információkat megkaptuk, ' +
                  'kollégánk átnézi és jelentkezni fog. A fizetés még nem esedékes: kollégánk ' +
                  'visszajelzése után e-mailben küldjük a végleges árat és a fizetési linket. ' +
                  ANSWER_PROMISE,
              },
              /* The promise used to be a band of its own under the body.
                 §14 puts it **inside** the body, as its last sentence, and
                 the band would then say it twice. `ANSWER_PROMISE` is the
                 one string either way, so the two can never differ and the
                 other templates that carry the band are untouched. */
              ...osszesites(order),
              publicLink(order, 'Megrendelés megtekintése'),
              ...termsNote(payload),
              signOff,
            ]),
          }),

  U22: (payload) =>
    withOrder(payload, (order) =>
      payload.lang === 'en'
        ? CUSTOMER_EN['U22']!(payload)
        : {
            subject: `Visszaigazoltuk az ajánlatát · ${order.ref}`,
            preheader: `${ft(order.gross)} – a fizetési link ebben a levélben van.`,
            blocks: keep([
              greeting(order),
              {
                kind: 'band',
                text: 'Átnéztük a kérését, és az alábbi ajánlatot visszaigazoltuk.',
                tone: 'good',
              },
              ...osszesites(order),
              confirmPayDeadline(order, 'hu'),
              publicLink(order, 'Fizetés'),
              declineLink(order, 'hu'),
              ...termsNote(payload),
              signOff,
            ]),
          }),

  // §9.6: "Payment window or deadline passes → cancelled · payment_expired,
  // U7". No storno: a díjbekérő needs none.
  U7: (payload) =>
    withOrder(payload, (order) => ({
      subject: `A rendelés fizetési határideje lejárt · ${order.ref}`,
      preheader: 'A fizetés nem érkezett meg határidőre, ezért a rendelés lezárult.',
      blocks: keep([
        greeting(order),
        {
          kind: 'paragraph',
          text:
            'A rendeléséhez tartozó fizetés a határidőig nem érkezett meg, ezért a rendelést lezártuk ' +
            'és az időpontot felszabadítottuk. Nem keletkezett fizetési kötelezettsége.',
        },
        recap(order),
        order.payment_deadline
          ? { kind: 'facts', rows: [['Fizetési határidő volt', dateTime(order.payment_deadline)]] }
          : null,
        {
          kind: 'paragraph',
          text: 'Ha továbbra is szeretne rendelni, indítson új rendelést az oldalunkon.',
        },
        signOff,
      ]),
    })),

  // §9.6 *Modification after confirmation*: "Nothing changes for the customer
  // until U10 or U11."
  U10: (payload) =>
    withOrder(payload, (order) => ({
      subject: `A módosítás érvénybe lépett · ${order.ref}`,
      preheader: 'A rendelése a módosítással együtt érvényes. Az új összeállítás alább.',
      blocks: keep([
        greeting(order),
        { kind: 'band', text: 'A kért módosítást érvénybe léptettük.', tone: 'good' },
        // build/FIXES.md item 93 (ruling 6 of 9 October (2)): U10 carries the
        // order summary of U5, for the new version. `osszesites` is the one
        // shared part both emails render, and the payload's order is the
        // outbox row's version -- the new one -- with its own lines.
        ...osszesites(order),
        payload.fee !== null ? { kind: 'heading', text: 'Módosítási díj' } : null,
        feeBlock(payload),
        // An old order's storno and reissued számla, as before. A new order's
        // same- or lower-price change issues no document, and a top-up's
        // előlegszámla is szamlazz.hu's to send (item 87): U10 does not carry
        // it. The heading is shown only above something.
        invoiceBlock(payload, 'storno') !== null || invoiceBlock(payload, 'szamla') !== null
          ? { kind: 'heading', text: 'Számlák' }
          : null,
        invoiceBlock(payload, 'storno'),
        invoiceBlock(payload, 'szamla'),
        payload.payments.some((payment) => payment.kind === 'refund')
          ? { kind: 'paragraph', text: refundSentence(payload) }
          : null,
        publicLink(order),
        signOff,
      ]),
    })),

  // §9.6 *Cancellation*, customer. Before freeze full refund; after freeze the
  // fee of §11.8 and the refund net of it.
  U11: (payload) =>
    withOrder(payload, (order) => ({
      subject: `A lemondás érvénybe lépett · ${order.ref}`,
      preheader: 'A rendelést lemondtuk. A pénzügyi részletek alább.',
      blocks: keep([
        greeting(order),
        { kind: 'paragraph', text: 'A kérésének megfelelően a rendelést lemondtuk.' },
        recap(order),
        payload.fee !== null ? { kind: 'heading', text: 'Lemondási díj' } : null,
        feeBlock(payload),
        payload.fee !== null ? feeLadderBlock(payload, 'note') : null,
        invoiceBlock(payload, 'storno'),
        { kind: 'paragraph', text: refundSentence(payload) },
        signOff,
      ]),
    })),

  // §11.5 U12: "day before: slot or pickup, and one line that the food arrives
  // ready to set out, in our boxes or on the client's own plates".
  U12: (payload) =>
    withOrder(payload, (order) => ({
      // B73: the handover day by name, never *holnap*. U12 goes out on the
      // business day before the handover, which after a weekend or a holiday
      // is not the day before.
      subject: (() => {
        const day = onDay(order.fulfilment_date ?? order.event_date)
        const Day = day.charAt(0).toUpperCase() + day.slice(1)
        return order.fulfilment === 'pickup'
          ? `${Day} várjuk átvételre · ${order.ref}`
          : `${Day} érkezünk · ${order.ref}`
      })(),
      preheader: fulfilmentWhen(order, 'hu') || date(order.event_date, true),
      blocks: keep([
        greeting(order),
        {
          kind: 'facts',
          rows: [
            ['Rendelésazonosító', order.ref],
            ['Dátum', date(order.event_date, true)],
            order.fulfilment === 'pickup'
              /* Run 16 item 2: the day-before reminder names the day as well
                 as the window — the collection day, which for a weekend event
                 is not the event date. */
              ? (['Személyes átvétel', fulfilmentWhen(order, 'hu') || 'a megbeszélt időpontban'] as [string, string])
              : (['Kiszállítási idősáv', fulfilmentWhen(order, 'hu') || 'a megbeszélt idősávban'] as [string, string]),
            ['Vendégek', `${n(order.guests)} fő`],
          ],
        },
        {
          kind: 'paragraph',
          text:
            'Az étel tálalásra készen érkezik: a saját dobozainkban, amelyekből közvetlenül kínálható, ' +
            'vagy az Ön tányérjaira rendezve, ahogy megbeszéltük.',
        },
        order.gf_guests > 0 ? { kind: 'note', text: GLUTEN_FREE_NOTICE } : null,
        // B74 (31 October 2026): the self-service cross-contact sentence (§14)
        // and the handover point and consume-by sentence (B08, B75) are not
        // written yet. An unwritten slot shows nothing to a customer; it used
        // to print *HIÁNYZÓ SZÖVEG*, which reached Anikó's test inbox.
        signOff,
      ]),
    })),

  // §11.10: the five optional questions, and the RevoBox collection.
  U13: (payload) =>
    withOrder(payload, (order) => ({
      subject: `Hogy sikerült? · ${order.ref}`,
      preheader: 'Öt rövid kérdés, és a RevoBox dobozok visszavétele.',
      blocks: keep([
        greeting(order),
        {
          kind: 'paragraph',
          text:
            'Köszönjük, hogy minket választott. Ha van rá egy perce, öt rövid kérdésre adott válasza ' +
            'sokat segít abban, hogy legközelebb pontosabban becsüljük a mennyiséget. Minden kérdés ' +
            'kihagyható.',
        },
        {
          kind: 'list',
          items: [
            'Hányan voltak végül?',
            'Milyen volt a mennyiség? (sok maradt · kicsit maradt · épp elég volt · hamar elfogyott · kevés volt)',
            'Mi fogyott el elsőként?',
            'Miből maradt a legtöbb?',
            'Rendelne tőlünk újra?',
          ],
        },
        // §11.10's survey, which is its own page and its own token. `public_url`
        // is §9.4's payment return page: sending a customer there to answer
        // five questions about an event that is over was a defect, not a
        // fallback, so a missing link is no link at all.
        typeof payload.digest?.['survey_url'] === 'string'
          ? { kind: 'button', label: 'Kérdőív kitöltése', href: payload.digest['survey_url'] as string }
          : { kind: 'note', text: 'HIÁNYZÓ ADAT: a kérdőív hivatkozása nem érkezett meg az értesítéssel.' },
        typeof payload.digest?.['revobox_out'] === 'number' && (payload.digest['revobox_out'] as number) > 0
          ? {
              kind: 'paragraph',
              text: `A kiszállított ${n(payload.digest['revobox_out'] as number)} RevoBox dobozért kollégánk jelentkezik.`,
            }
          : null,
        // Item 93: U13 neither carries nor waits for the végszámla, which
        // szamlazz.hu emails on its own; this is the empty slot for the
        // sentence saying so.
        copySlot('U13_invoice_from_szamlazz'),
        signOff,
      ]),
    })),

  // §11.9 and §11.5 U14: a corrected allergen list on an open order.
  U14: (payload) =>
    withOrder(payload, (order) => ({
      subject: `Pontosított allergénlista · ${order.ref}`,
      preheader: 'Egy fogás allergénadatát pontosítottuk. Az új lista alább.',
      blocks: keep([
        greeting(order),
        {
          kind: 'paragraph',
          text:
            'A rendelésében szereplő egyik fogás allergénadata pontosult. Az étel és az ár nem ' +
            'változott; az aktuális lista az alábbi.',
        },
        recap(order),
        {
          kind: 'table',
          head: ['Fogás', 'Allergének'],
          rows: order.lines.map((line) => [dishNameCell(line, '', {}, emailLang(order.lang)), { text: allergenTextLong(line) }]),
        },
        lookalikeNote(order.lines, emailLang(order.lang)),
        order.gf_guests > 0 ? { kind: 'note', text: GLUTEN_FREE_NOTICE } : null,
        {
          kind: 'paragraph',
          text: 'Ha ez bármit megváltoztat az Ön oldalán, kérjük, válaszoljon erre a levélre.',
        },
        signOff,
      ]),
    })),

  // §11.8: "The customer asks by replying to any order email … A request
  // counts from when it arrives." U15 is the acknowledgement of arrival.
  U15: (payload) =>
    withOrder(payload, (order) => ({
      subject: `Megkaptuk a módosítási kérését · ${order.ref}`,
      preheader: 'A kérés beérkezett. A rendelés addig változatlan.',
      blocks: keep([
        greeting(order),
        {
          kind: 'paragraph',
          text:
            'A módosítási kérése megérkezett hozzánk. A kérés a beérkezésének időpontjától számít. ' +
            'Kollégánk átnézi, és visszajelzünk, mielőtt bármi megváltozna.',
        },
        recap(order),
        { kind: 'facts', rows: [['A kérés beérkezett', dateTime(payload.created_at)]] },
        { kind: 'paragraph', text: 'A rendelése addig változatlanul érvényes.' },
        feeLadderBlock(payload, 'note'),
        signOff,
      ]),
    })),

  // §9.6 *Cancellation*, Planteen. Never a fee; release before capture, full
  // refund once money has been received.
  U16: (payload) =>
    withOrder(payload, (order) => ({
      subject: `Sajnáljuk: a rendelését le kellett mondanunk · ${order.ref}`,
      preheader: 'A rendelést a Planteen mondta le. Önnek nincs fizetési kötelezettsége.',
      blocks: keep([
        greeting(order),
        {
          kind: 'paragraph',
          text:
            'Sajnáljuk: a rendelését a mi oldalunkon felmerült ok miatt le kellett mondanunk. ' +
            'Önnek semmilyen díjat nem számítunk fel.',
        },
        recap(order),
        // `build/FIXES.md` item 83 (P4): with no refund, U16 said the amount
        // reserved on the card had been released; that sentence is deleted
        // with nothing in its place. The refund sentence is unchanged.
        payload.payments.some((payment) => payment.kind === 'refund')
          ? { kind: 'paragraph', text: refundSentence(payload) }
          : null,
        invoiceBlock(payload, 'storno'),
        { kind: 'paragraph', text: 'Ha másik időpont szóba jöhet, keressen minket.' },
        signOff,
      ]),
    })),

  // §5.7: the acknowledgement. Run 9, item 6 (DECIDED 20261010): the sentence
  // that nothing is reserved ("nem foglaltunk le időpontot…") is removed, as
  // it was from the page; the preheader's echo of it goes with it.
  U17: (payload) => ({
    subject: 'Megkaptuk a visszahívási kérését',
    preheader: 'Hamarosan keressük.',
    blocks: keep([
      {
        kind: 'paragraph',
        text: payload.callback?.name ? `Kedves ${payload.callback.name}!` : 'Kedves Vendégünk!',
      },
      {
        kind: 'paragraph',
        text:
          'Köszönjük, hogy jelezte: szívesen átbeszélné a mennyiségeket. Kollégánk hamarosan ' +
          'felhívja a megadott számon.',
      },
      payload.callback
        ? {
            kind: 'facts',
            rows: [
              ['Telefonszám', payload.callback.phone ?? ''],
              ['Beérkezett', dateTime(payload.callback.created_at)],
            ],
          }
        : null,
      payload.site.catering_phone
        ? {
            kind: 'paragraph',
            text: `Ha előbb szeretne beszélni velünk, hívjon minket: ${payload.site.catering_phone}.`,
          }
        : null,
      signOff,
    ]),
  }),

  // §5.7 *Expiry*: `callback_expiry_bd` 3 business days or the freeze point,
  // whichever comes first. "U18 tells the customer it has lapsed and how to
  // order."
  U18: (payload) => ({
    subject: 'A visszahívási kérése lejárt',
    preheader: 'Nem sikerült elérnünk. Rendelni bármikor tud az oldalunkon.',
    blocks: keep([
      {
        kind: 'paragraph',
        text: payload.callback?.name ? `Kedves ${payload.callback.name}!` : 'Kedves Vendégünk!',
      },
      {
        kind: 'paragraph',
        text:
          'A visszahívási kérése lejárt. Elnézést kérünk, ha nem sikerült elérnünk Önt. ' +
          'Rendelését bármikor leadhatja az oldalunkon, és ha szeretné, telefonon is segítünk.',
      },
      payload.site.catering_phone
        ? { kind: 'facts', rows: [['Telefonszámunk', payload.site.catering_phone]] }
        : null,
      payload.site.site_url
        ? { kind: 'button', label: 'Rendelés leadása', href: payload.site.site_url }
        : null,
      signOff,
    ]),
  }),

  // §9.6 *Modification after confirmation*, Higher: "U19 with the payment
  // link. Paid: apply, storno, new számla, U10. Unpaid at expiry: the change
  // lapses, the original stands, U20, C13."
  U19: (payload) =>
    withOrder(payload, (order) => ({
      subject: `Fizetési link a módosításhoz · ${order.ref}`,
      preheader: payload.pending_change
        ? `Fizetendő ${ft(payload.pending_change.amount_due)}.`
        : 'A módosítás különbözete fizetésre vár.',
      blocks: keep([
        greeting(order),
        {
          kind: 'paragraph',
          text:
            'A kért módosítás az eredetinél magasabb összeget jelent. A különbözet kifizetése után ' +
            'a módosítás érvénybe lép; addig az eredeti rendelés él.',
        },
        payload.pending_change
          ? {
              kind: 'facts',
              rows: keep2([
                ['Fizetendő különbözet', ft(payload.pending_change.amount_due)],
                payload.pending_change.expires_at
                  ? ['A link érvényessége', dateTime(payload.pending_change.expires_at)]
                  : null,
              ]),
            }
          : null,
        payload.fee !== null ? { kind: 'heading', text: 'Módosítási díj' } : null,
        feeBlock(payload),
        payload.pending_change?.payment_ref
          ? {
              kind: 'button',
              label: 'Különbözet kifizetése',
              href: payload.pending_change.payment_ref,
            }
          : {
              kind: 'note',
              text: 'HIÁNYZÓ ADAT: a fizetési link még nem állt elő ehhez a módosításhoz.',
            },
        { kind: 'heading', text: 'A módosított összeállítás' },
        ...osszesites(order),
        {
          kind: 'note',
          text:
            'Ha a link lejártáig nem érkezik meg a különbözet, a módosítás nem lép érvénybe, és az ' +
            'eredeti rendelés marad hatályban.',
        },
        signOff,
      ]),
    })),

  // §9.6: "Unpaid at expiry: the change lapses, the original stands, U20, C13."
  U20: (payload) =>
    withOrder(payload, (order) => ({
      subject: `A módosítás nem lépett érvénybe · ${order.ref}`,
      preheader: 'A különbözet nem érkezett meg, ezért az eredeti rendelés marad.',
      blocks: keep([
        greeting(order),
        {
          kind: 'paragraph',
          text:
            'A módosításhoz tartozó különbözet a fizetési link lejártáig nem érkezett meg, ezért a ' +
            'módosítás nem lépett érvénybe. Az eredeti rendelése változatlanul él, az alábbiak szerint.',
        },
        ...osszesites(order),
        {
          kind: 'paragraph',
          text: 'Ha továbbra is szeretné a módosítást, válaszoljon erre a levélre, és új linket küldünk.',
        },
        publicLink(order),
        signOff,
      ]),
    })),
}

/** `keep` for fact rows. */
function keep2(rows: ([string, string] | null)[]): [string, string][] {
  return rows.filter((row): row is [string, string] => row !== null)
}

/* ------------------------------------------------------------------ *
 * Internal, catering@planteen.hu (§11.5)
 * ------------------------------------------------------------------ */

const internal: Record<string, Template> = {
  C1: (payload) =>
    withOrder(payload, (order) => ({
      subject: `[C1] Visszaigazolva · ${order.ref} · ${date(order.event_date)}`,
      preheader: `${n(order.guests)} vendég · ${ft(order.gross)} · felülvizsgálat nélkül`,
      blocks: keep([
        { kind: 'paragraph', text: 'Rendelés visszaigazolva, felülvizsgálat nélkül.' },
        internalHeader(payload, order),
        moneySummary(order),
        consoleButton(payload),
      ]),
    })),

  /* `build/FIXES.md` item 79 (F8): "The staff notification reuses C2, with
     the comment text and the reasons."

     One code, two things it can be about, and the band says which: an order
     in the pay-first review lane of §11.4 — where the money is already
     secured — or a request in `awaiting_confirmation`, where nothing has been
     paid at all. Telling a colleague the money is secured on an order that
     has not been paid would be the worst thing this email could do, so the
     band is read off `confirmation_reasons` and not off the status. */
  C2: (payload) =>
    withOrder(payload, (order) => {
      const forConfirmation = (payload.internal?.confirmation_reasons ?? []).length > 0
      /* Run 13a item 2: a late transfer is already paid, so the band says so. */
      const latePayment = (payload.internal?.confirmation_reasons ?? []).includes('late_payment')
      return {
        subject: forConfirmation
          ? `[C2] Visszaigazolásra vár · ${order.ref} · ${date(order.event_date)}`
          : `[C2] Felülvizsgálatra vár · ${order.ref} · ${date(order.event_date)}`,
        preheader: `${n(order.guests)} vendég · ${ft(order.gross)} · ${ANSWER_PROMISE_SHORT}`,
        blocks: keep([
          {
            kind: 'band',
            text: latePayment
              ? 'Visszaigazolásra vár. Az átutalás a határidő után érkezett meg: jóváhagyáskor a rendelés visszaigazolt, elutasításkor az összeg visszatérítendő.'
              : forConfirmation
              ? 'Visszaigazolásra vár. Fizetés még nem történt: a vendég a visszaigazolás után fizet.'
              : payload.payments.some((p) => p.method === 'card_dijbekero')
              ? 'Felülvizsgálatra vár. A fizetés beérkezett (bankkártyával, a díjbekérő alapján).'
              : 'Felülvizsgálatra vár. A fizetés biztosítva (kártyán zárolva vagy átutalás beérkezett).',
            tone: 'warn',
          },
          internalHeader(payload, order),
          forConfirmation
            ? { kind: 'heading', text: 'Visszaigazolási okok (build/FIXES.md 79.)' }
            : { kind: 'heading', text: 'Felülvizsgálati okok (§11.4)' },
          forConfirmation
            ? confirmationReasons(payload)
            : (reviewReasons(payload) ?? { kind: 'note', text: 'Nincs rögzített ok.' }),
          /* The automatic review reasons, where there are also some: ruling 9
             puts the order in `awaiting_confirmation` "whether or not an
             automatic review reason is also present", and the approving
             colleague clears both. */
          forConfirmation ? reviewReasons(payload) : null,
          payload.internal?.customer_comment
            ? { kind: 'heading', text: 'A vendég megjegyzése' }
            : null,
          payload.internal?.customer_comment
            ? { kind: 'paragraph', text: payload.internal.customer_comment }
            : null,
          payload.internal?.dietary_note
            ? { kind: 'heading', text: 'Étrendi megjegyzés — aláírás szükséges (K8)' }
            : null,
          payload.internal?.dietary_note
            ? { kind: 'paragraph', text: payload.internal.dietary_note }
            : null,
          moneySummary(order),
          { kind: 'note', text: ANSWER_PROMISE },
          consoleButton(payload, forConfirmation ? 'Kérés megnyitása' : 'Felülvizsgálat megnyitása'),
        ]),
      }
    }),

  C3: (payload) =>
    withOrder(payload, (order) => ({
      subject: `[C3] Emlékeztető: felülvizsgálat vár · ${order.ref}`,
      preheader: 'Három munkaórája vár felülvizsgálatra.',
      blocks: keep([
        { kind: 'band', text: 'Három munkaórája vár felülvizsgálatra (§11.4).', tone: 'warn' },
        internalHeader(payload, order),
        reviewReasons(payload),
        { kind: 'note', text: ANSWER_PROMISE },
        consoleButton(payload, 'Felülvizsgálat megnyitása'),
      ]),
    })),

  C4: (payload) =>
    withOrder(payload, (order) => ({
      subject: `[C4] Eszkaláció: felülvizsgálat nem történt meg · ${order.ref}`,
      preheader: 'Négy munkaórája vár felülvizsgálatra.',
      blocks: keep([
        {
          kind: 'band',
          text: 'Négy munkaórája vár felülvizsgálatra. A válaszidő veszélyben van (§11.4).',
          tone: 'stop',
        },
        internalHeader(payload, order),
        reviewReasons(payload),
        consoleButton(payload, 'Felülvizsgálat megnyitása'),
      ]),
    })),

  // §5.9: an enquiry is not an order, so this code carries the enquiry row.
  C5: (payload) => ({
    subject: `[C5] Új érdeklődés${payload.enquiry?.name ? ` · ${payload.enquiry.name}` : ''}`,
    preheader: 'Érdeklődés érkezett a weboldalról.',
    blocks: keep([
      { kind: 'paragraph', text: 'Érdeklődés érkezett a weboldalról. Ez nem rendelés.' },
      payload.enquiry
        ? {
            kind: 'facts',
            rows: keep2([
              ['Név', payload.enquiry.name ?? ''],
              ['E-mail', payload.enquiry.email ?? ''],
              ['Telefon', payload.enquiry.phone ?? ''],
              ['Beérkezett', dateTime(payload.enquiry.created_at)],
              payload.enquiry.reasons.length > 0
                ? [
                    'Miért ide került',
                    payload.enquiry.reasons
                      .map((reason) => ENQUIRY_REASON_LABEL[reason] ?? reason)
                      .join(' · '),
                  ]
                : null,
            ]),
          }
        : { kind: 'note', text: 'HIÁNYZÓ ADAT: az érdeklődés sora nem olvasható.' },
      payload.enquiry?.message ? { kind: 'heading', text: 'Üzenet' } : null,
      payload.enquiry?.message ? { kind: 'paragraph', text: payload.enquiry.message } : null,
      consoleButton(payload, 'Érdeklődés megnyitása'),
    ]),
  }),

  C7: (payload) =>
    withOrder(payload, (order) => ({
      subject: `[C7] Fizetési határidő lejárt · ${order.ref}`,
      preheader: 'A rendelés lezárult, a kapacitás felszabadult.',
      blocks: keep([
        {
          kind: 'band',
          text: 'A fizetési határidő lejárt. A rendelés lezárult, a kapacitás felszabadult.',
          tone: 'warn',
        },
        internalHeader(payload, order),
        order.payment_deadline
          ? { kind: 'facts', rows: [['Fizetési határidő volt', dateTime(order.payment_deadline)]] }
          : null,
        { kind: 'note', text: 'Díjbekérőhöz nem jár sztornó (§9.6).' },
        consoleButton(payload),
      ]),
    })),

  // §19.4 `sweep_timers` freezes; C10 is the digest of what froze that day.
  C10: (payload) => ({
    subject: `[C10] Napi véglegesítési összesítő · ${date(payload.for_date ?? payload.created_at)}`,
    preheader: 'A ma véglegesített rendelések.',
    blocks: digestBlocks(payload, 'A ma véglegesített rendelések.', [
      'Rendelés',
      'Rendezvény dátuma',
      'Vendégek',
      'Adag',
    ]),
  }),

  C11: (payload) => ({
    subject: `[C11] Kapacitásküszöb · ${date(payload.for_date ?? payload.created_at)}`,
    preheader: 'Egy nap elérte a lágy kapacitásküszöböt.',
    blocks: keep([
      {
        kind: 'band',
        text: 'Egy rendezvénynap elérte a lágy kapacitásküszöböt (§11.4).',
        tone: 'warn',
      },
      digestFacts(payload),
      consoleButton(payload, 'Naptár megnyitása'),
    ]),
  }),

  C12: (payload) =>
    withOrder(payload, (order) => ({
      subject: `[C12] Katalógusváltozás érinti ezt a rendelést · ${order.ref}`,
      preheader: 'Allergén, gluténmentesség vagy visszavonás változott egy fogáson.',
      blocks: keep([
        {
          kind: 'band',
          text: 'Katalógusváltozás érint egy nyitott rendelést. A címkézés letiltva, amíg valaki dönt (§11.9).',
          tone: 'stop',
        },
        internalHeader(payload, order),
        catalogueChangeTable(payload),
        consoleButton(payload, 'Katalógus-riasztás megnyitása'),
      ]),
    })),

  C13: (payload) =>
    withOrder(payload, (order) => ({
      subject: `[C13] Módosítás érvénybe lépett · ${order.ref}`,
      preheader: 'Az új verzió él; a pénzügyi rész rendezve.',
      blocks: keep([
        { kind: 'paragraph', text: 'Módosítás érvénybe lépett, vagy egy módosítás lejárt és az eredeti maradt.' },
        internalHeader(payload, order),
        payload.pending_change
          ? {
              kind: 'facts',
              rows: [
                ['Javasolt verzió', String(payload.pending_change.proposed_version)],
                [
                  'Állapota',
                  PENDING_CHANGE_STATUS_LABEL[payload.pending_change.status] ?? payload.pending_change.status,
                ],
                ['Különbözet', ft(payload.pending_change.amount_due)],
              ],
            }
          : null,
        feeBlock(payload),
        moneySummary(order),
        consoleButton(payload),
      ]),
    })),

  C14: (payload) => ({
    subject: `[C14] Átadás nincs rögzítve · ${date(payload.for_date ?? payload.created_at)}`,
    preheader: 'Tegnapi rendezvény átadása nincs rögzítve.',
    blocks: digestBlocks(payload, 'Ezeknél a rendeléseknél az átadás nincs rögzítve (§11.2).', [
      'Rendelés',
      'Rendezvény dátuma',
      'Vendégek',
      'Adag',
    ]),
  }),

  C15: (payload) => ({
    subject: `[C15] Lezárás hiányos · ${date(payload.for_date ?? payload.created_at)}`,
    preheader: 'Maradék vagy RevoBox nincs rögzítve.',
    blocks: digestBlocks(payload, 'Ezeknél a rendeléseknél a lezárás hiányos (§11.10).', [
      'Rendelés',
      'Rendezvény dátuma',
      'Vendégek',
      'Adag',
    ]),
  }),

  C16: (payload) =>
    withOrder(payload, (order) => ({
      subject: `[C16] A javasolt módosításra nincs válasz · ${order.ref}`,
      preheader: 'Egy munkanapja nincs válasz a javaslatunkra.',
      blocks: keep([
        {
          kind: 'band',
          text:
            'Egy munkanapja nincs válasz a Planteen által javasolt módosításra (§11.8). ' +
            '48 órán belüli rendezvénynél telefonálni kell.',
          tone: 'warn',
        },
        internalHeader(payload, order),
        payload.pending_change
          ? {
              kind: 'facts',
              rows: [
                ['Javasolt verzió', String(payload.pending_change.proposed_version)],
                ['Különbözet', ft(payload.pending_change.amount_due)],
              ],
            }
          : null,
        consoleButton(payload),
      ]),
    })),

  // §5.7: "C17 to the Head of Events & Catering (`catering` role): basket,
  // phone, action *call the customer*."
  C17: (payload) => ({
    subject: `[C17] Visszahívást kért${payload.callback?.name ? ` · ${payload.callback.name}` : ''}`,
    preheader: 'Teendő: hívja vissza a vendéget.',
    blocks: keep([
      { kind: 'band', text: 'Teendő: hívja vissza a vendéget.', tone: 'warn' },
      payload.callback
        ? {
            kind: 'facts',
            rows: keep2([
              ['Név', payload.callback.name ?? ''],
              ['Telefon', payload.callback.phone ?? ''],
              ['E-mail', payload.callback.email ?? ''],
              ['Beérkezett', dateTime(payload.callback.created_at)],
              payload.callback.expires_at ? ['Lejár', dateTime(payload.callback.expires_at)] : null,
            ]),
          }
        : { kind: 'note', text: 'HIÁNYZÓ ADAT: a visszahívási kérés sora nem olvasható.' },
      { kind: 'heading', text: 'A kosár' },
      basketTable(payload),
      {
        kind: 'note',
        text: 'A visszahívási kérés nem rendelés: nincs kapacitásfoglalás és nem futnak rendelési szabályok (§5.7).',
      },
      consoleButton(payload, 'Visszahívás megnyitása'),
    ]),
  }),

  // §11.5: A1 and A2 go to admin, whose address is OPEN in rev7.
  A1: (payload) => ({
    subject: '[A1] Levélküldés ötször sikertelen',
    preheader: 'Egy kimenő levél ötször nem ment ki.',
    blocks: keep([
      {
        kind: 'band',
        text: 'Egy értesítés ötször nem ment ki. A sor sikertelen állapotban áll és nem próbálkozik tovább.',
        tone: 'stop',
      },
      {
        kind: 'facts',
        rows: keep2([
          ['Kód', String(payload.digest?.['failed_code'] ?? '')],
          ['Kimenő sor azonosítója', String(payload.digest?.['failed_outbox_id'] ?? '')],
          ['Címzett', String(payload.digest?.['failed_to'] ?? '')],
          ['Próbálkozások', String(payload.digest?.['attempts'] ?? '')],
          payload.digest?.['error'] ? ['Hiba', String(payload.digest['error'])] : null,
        ]),
      },
      payload.order ? internalHeader(payload, payload.order) : null,
      consoleButton(payload, 'Kimenő levelek megnyitása'),
    ]),
  }),

  /* A2 has two occasions. §11.12.4's is the roles sheet; A4 of the catalogue
     brief of 19 October 2026 gives the same code to a catalogue Sheet that
     refused, could not be read, or failed. The digest says which, so one
     code does not read as the other. */
  A2: (payload) => {
    const catalogue = (payload.digest as Record<string, unknown> | null)?.kind === 'catalogue'
    return {
      subject: catalogue
        ? '[A2] A katalógus szinkronja elutasította a táblázatot'
        : '[A2] A jogosultság-szinkron elutasította a változást',
      preheader: catalogue
        ? 'Az oldalon a legutóbbi jó állapot maradt.'
        : 'A védőszabály megállította a staff-szinkront.',
      blocks: keep([
        {
          kind: 'band',
          text: catalogue
            ? 'A katalógus szinkronja nem tudta közzétenni a táblázatot. Az oldalon a legutóbbi jó ' +
              'állapot maradt: a kínálat nem változott, rendelést ez nem akadályoz. Az okot alább olvassa.'
            : 'A hozzáférési táblázat szinkronja elutasította a változást: a védőszabály nem engedi, hogy ' +
              'admin vagy allergén-aláíró nélkül maradjon a rendszer (§11.12.4).',
          tone: 'stop',
        },
        digestFacts(payload),
        consoleButton(payload, catalogue ? 'Katalógus megnyitása' : 'Beállítások megnyitása'),
      ]),
    }
  },

  /* A3 (DECIDED 20261019, Barna), the catalogue brief's allergen guard. A1
     *level*, not the A1 code: A1 is "a send failed five times", and reading
     the two as one would hide this one. It goes to admin and to every active
     allergen signatory, and names the dish and each field that moved. */
  A3: (payload) => ({
    subject: '[A3] Allergénadat változott hitelesítés nélkül: a fogás visszavonva',
    preheader: 'A fogás lekerült a kínálatból, amíg valaki újra nem hitelesíti.',
    blocks: keep([
      {
        kind: 'band',
        text:
          'Egy közzétett fogás allergénadata megváltozott a katalógusban, de sem a Hitelesítette, ' +
          'sem a Hitelesítés dátuma nem változott vele. A fogást visszavontuk a kínálatból. ' +
          'Akkor kerül vissza, amikor egy szinkron a változást új hitelesítővel vagy új ' +
          'hitelesítési dátummal együtt látja.',
        tone: 'stop',
      },
      digestFacts(payload),
      {
        kind: 'note',
        text:
          'A már leadott rendeléseket ez nem érinti: minden rendelés a leadáskori allergénlistát őrzi.',
      },
      consoleButton(payload, 'Katalógus megnyitása'),
    ]),
  }),

  /* A4 (SimplePay test, 20261103), SP doc §3.14: AUTHORIZED or REVERSED on a
     one-step charge, which this system never asks for. The IPN was
     acknowledged so SimplePay stops resending it; nothing on the order moved. */
  A4: (payload) =>
    withOrder(payload, (order) => ({
      subject: `[A4] SimplePay-értesítés váratlan státusszal · ${order.ref}`,
      preheader: 'Az értesítést nyugtáztuk, a rendelés nem változott.',
      blocks: keep([
        {
          kind: 'band',
          text:
            'A SimplePay egylépcsős terheléshez nem várt státuszt küldött (AUTHORIZED vagy REVERSED). ' +
            'Az értesítést nyugtáztuk, a rendelés és a fizetés állapota nem változott.',
          tone: 'warn',
        },
        { kind: 'paragraph', text: 'Kérjük, nézze meg a tranzakciót a SimplePay kereskedői felületén.' },
        consoleButton(payload, 'Rendelés megnyitása'),
      ]),
    })),
}

/* ------------------------------------------------------------------ *
 * Finance, finance@planteen.hu (§11.5)
 * ------------------------------------------------------------------ */

/** Item 86's hold reasons, as `public.invoice_final_hold_reasons` and
 *  `invoice_jobs` write them. Staff wording, for Finance. */
const HOLD_REASON_LABEL: Record<string, string> = {
  advances_differ: 'az élő előlegszámlák összege eltér az érvényes verzió bruttójától',
  open_refund: 'nyitott visszatérítés van a rendelésen',
  unpaid_topup: 'kifizetetlen különbözet van a rendelésen',
  open_change: 'függőben lévő módosítás van a rendelésen',
  agent_one_advance_per_final: 'a szamlazz.hu egy végszámlával csak egy előlegszámlát zár le',
}

const finance: Record<string, Template> = {
  F1: (payload) =>
    withOrder(payload, (order) => ({
      subject: `[F1] Átutalási határidő fizetés nélkül telt le · ${order.ref}`,
      preheader: 'A rendelés lezárult. Ha az utalás később mégis megérkezik, rögzítse a rendelés oldalán.',
      blocks: keep([
        { kind: 'band', text: 'Az átutalási határidő fizetés nélkül telt le.', tone: 'warn' },
        internalHeader(payload, order),
        moneySummary(order),
        { kind: 'note', text: 'Díjbekérőhöz nem jár sztornó (§9.6).' },
        /* Run 13a item 2 (DECIDED 20261012): a late transfer is recorded on
           the order page (the link below opens its Pénzmozgás section) and
           goes to the confirmations view as late_payment. */
        {
          kind: 'note',
          text:
            'Ha a pénz a határidő után mégis megérkezik, rögzítse a rendelés oldalán (Pénzmozgás). ' +
            'A rendelés ekkor visszaigazolásra vár: jóváhagyáskor visszaigazolt lesz, és az előlegszámla ' +
            'a jóváírás napjával készül; elutasításkor az összeg visszatérítendő.',
        },
        consoleButton(payload),
      ]),
    })),

  F2: (payload) =>
    withOrder(payload, (order) => ({
      subject: `[F2] Kártyafoglalás vagy -terhelés sikertelen · ${order.ref}`,
      preheader: 'A rendelés fizetve állapotban maradt; a terhelés nem sikerült.',
      blocks: keep([
        {
          kind: 'band',
          text: 'Kártyaművelet sikertelen. A rendelés nem lépett tovább (§9.10 no. 14).',
          tone: 'stop',
        },
        internalHeader(payload, order),
        paymentsTable(payload),
        consoleButton(payload),
      ]),
    })),

  // §9.8: "Every refund is an F4 task carrying the order reference, lane,
  // amount, reason, the original payment reference and a due date."
  F4: (payload) =>
    withOrder(payload, (order) => ({
      subject: `[F4] Visszatérítendő összeg · ${order.ref}`,
      preheader: 'Visszatérítési feladat, határidővel.',
      blocks: keep([
        { kind: 'band', text: 'Visszatérítési feladat. Kézzel indítandó (§9.8).', tone: 'warn' },
        internalHeader(payload, order),
        refundTask(payload, order),
        feeBlock(payload),
        {
          kind: 'note',
          text:
            // Card payment change 5: a card payment on the díjbekérő is
            // refunded in szamlazz.hu / Teya, by hand.
            payload.payments.some((p) => p.method === 'card_dijbekero')
              ? 'Bankkártya a díjbekérőn: a szamlazz.hu / Teya felületén, az eredeti fizetés ellenében (§9.8).'
              : order.lane === 'transfer'
              ? 'Átutalás: az eredeti számlára, vagy a vevő által írásban megadott számlára (§9.8).'
              : 'Kártya: a SimplePay kereskedői felületén, az eredeti tranzakció ellenében (§9.8).',
        },
        consoleButton(payload, 'Visszatérítések megnyitása'),
      ]),
    })),

  // build/FIXES.md item 86: "raise F8 végszámla held to Finance with the order
  // reference, both amounts and the reason". §11.5's Finance series -- not
  // run 7's item label F8.
  F8: (payload) =>
    withOrder(payload, (order) => {
      const held = [...payload.invoices].reverse().find((i) => i.kind === 'vegszamla' && i.status === 'held')
      const advances = payload.invoices
        .filter((i) => i.kind === 'elolegszamla' && i.status === 'issued' && !i.reversed_at)
        .reduce((sum, i) => sum + i.gross, 0)
      const reasons = String(held?.error ?? '')
        .split(',')
        .map((code) => (code.split(':')[0] ?? '').trim())
        .filter((code) => code !== '')
      return {
        subject: `[F8] Végszámla visszatartva · ${order.ref}`,
        preheader: 'A végszámla nem készült el automatikusan. Kézi kiállítás szükséges.',
        blocks: keep([
          {
            kind: 'band',
            text:
              'A végszámlát a rendszer nem állította ki, mert az ellenőrzés eltérést talált. ' +
              'Kérjük, állítsa ki kézzel, és jelölje késznek a konzolon.',
            tone: 'warn',
          },
          internalHeader(payload, order),
          {
            kind: 'facts',
            rows: [
              ['Az érvényes verzió bruttó', ft(held?.gross ?? order.gross)],
              ['Élő előlegszámlák bruttó', ft(advances)],
              ['Ok', reasons.length === 0 ? '—' : reasons.map((code) => HOLD_REASON_LABEL[code] ?? code).join('; ')],
            ],
          },
          invoicesTable(payload),
          consoleButton(payload, 'Számlák megnyitása'),
        ]),
      }
    }),

  F5: (payload) =>
    withOrder(payload, (order) => ({
      subject: `[F5] A szamlazz.hu Agent ötször sikertelen · ${order.ref}`,
      preheader: 'A számla nem állt elő. A rendelés állapota nem változott.',
      blocks: keep([
        {
          kind: 'band',
          text:
            'A szamlazz.hu Agent ötször sikertelen volt. A rendelés állapotát ez nem érinti; a ' +
            'dokumentum hiányzik (§9.5).',
          tone: 'stop',
        },
        internalHeader(payload, order),
        // Document alert (20261024): the document and the address it should
        // have gone to, named outright, so Finance can act from szamlazz.hu.
        documentAlertFacts(
          order,
          payload.invoices.filter((i) => i.status === 'failed'),
        ),
        {
          kind: 'note',
          text:
            'Teendő: nézze meg a szamlazz.hu fiókban, létrejött-e a dokumentum. Ha igen, küldje el onnan ' +
            'kézzel a vevőnek. Ha nem, állítsa ki kézzel, és küldje el a fenti e-mail-címre.',
        },
        invoicesTable(payload),
        consoleButton(payload, 'Számlák megnyitása'),
      ]),
    })),

  // Document alert (20261024): the Agent issued the document, with a number,
  // and its answer carried no PDF. Not F5: nothing failed at the Agent.
  F9: (payload) =>
    withOrder(payload, (order) => {
      const issued = [...payload.invoices].reverse().find((i) => i.status === 'issued' && !i.pdf_path)
      return {
        subject: `[F9] Dokumentum PDF nélkül · ${order.ref}`,
        preheader: 'A szamlazz.hu kiállította a dokumentumot, de PDF-et nem küldött vissza.',
        blocks: keep([
          {
            kind: 'band',
            text:
              'A szamlazz.hu kiállította a dokumentumot, de a válaszában nem volt PDF. Nem biztos, hogy a ' +
              'vevő megkapta.',
            tone: 'warn',
          },
          internalHeader(payload, order),
          documentAlertFacts(order, issued === undefined ? [] : [issued]),
          {
            kind: 'note',
            text: 'Teendő: nyissa meg a dokumentumot a szamlazz.hu fiókban, és küldje el onnan kézzel a vevőnek.',
          },
          invoicesTable(payload),
          consoleButton(payload, 'Számlák megnyitása'),
        ]),
      }
    }),

  // Card payment change 4 (DECIDED 20261101): a card payment on the
  // díjbekérő was recorded with a szamlazz.hu előlegszámla number, and the
  // Agent query could not confirm it (kind, gross, order number). The payment
  // stands and the order moves on; the végszámla is held until it is checked.
  F10: (payload) =>
    withOrder(payload, (order) => {
      const advance = [...payload.invoices].reverse().find((i) => i.kind === 'elolegszamla' && i.status === 'issued')
      return {
        subject: `[F10] Előlegszámla ellenőrzése sikertelen · ${order.ref}`,
        preheader: 'A díjbekérőn kártyával fizetett rendelés előlegszámláját nem sikerült ellenőrizni.',
        blocks: keep([
          {
            kind: 'band',
            text:
              'A díjbekérőn bankkártyával fizetett rendeléshez rögzített szamlazz.hu előlegszámlát nem sikerült ' +
              'ellenőrizni. A fizetés rögzítve van, a rendelés halad tovább; a végszámla az ellenőrzésig vár.',
            tone: 'warn',
          },
          internalHeader(payload, order),
          documentAlertFacts(order, advance === undefined ? [] : [advance]),
          {
            kind: 'note',
            text:
              'Teendő: keresse meg az előlegszámlát a szamlazz.hu fiókban, és vesse össze a számát, a bruttó ' +
              'összegét és a rendelésszámát a rendeléssel. Az eltérés okát a konzol Számlák része mutatja.',
          },
          invoicesTable(payload),
          consoleButton(payload, 'Számlák megnyitása'),
        ]),
      }
    }),

  F6: (payload) =>
    withOrder(payload, (order) => ({
      subject: `[F6] Az átutalt összeg eltér · ${order.ref}`,
      preheader: 'A rendelés fizetésre vár állapotban marad.',
      blocks: keep([
        {
          kind: 'band',
          text: 'A beérkezett összeg eltér a rendelés összegétől. A rendelés fizetésre vár (§9.6).',
          tone: 'warn',
        },
        internalHeader(payload, order),
        {
          kind: 'facts',
          rows: [
            ['Rendelés bruttó', ft(order.gross)],
            ['Beérkezett', ft(Number(payload.digest?.['received'] ?? 0))],
            ['Eltérés', ft(Number(payload.digest?.['difference'] ?? 0))],
          ],
        },
        paymentsTable(payload),
        consoleButton(payload),
      ]),
    })),

  // §9.8 *Overdue*: "F7 to Finance and the managing director on the next
  // business day."
  F7: (payload) =>
    withOrder(payload, (order) => ({
      subject: `[F7] Lejárt visszatérítés · ${order.ref}`,
      preheader: 'A visszatérítés határideje letelt.',
      blocks: keep([
        { kind: 'band', text: 'A visszatérítés határideje letelt (§9.8).', tone: 'stop' },
        internalHeader(payload, order),
        refundTask(payload, order),
        consoleButton(payload, 'Visszatérítések megnyitása'),
      ]),
    })),
}

/* ------------------------------------------------------------------ *
 * Kitchen, kitchen@planteen.hu (§11.5, §11.6)
 *
 * §11.6 asks for every kitchen output as "table-based HTML email … plus the
 * A4 print view". They are two renders of one document, so both read the one
 * reading the database builds — `public.kitchen_doc` for the six codes about
 * an order, `public.kitchen_day_doc` for the two about a day — which arrives
 * on the payload as `digest.document`. The blocks are in `kitchenDoc.ts`;
 * what is here is which blocks each code puts in which order, and the
 * Hungarian around them.
 *
 * A payload with no document still renders: a row written before
 * 043_kitchen_docs carries none, and the preview page renders a sample. Those
 * fall back to the order payload's own dish table and say in one line that the
 * full document could not be read, rather than printing an empty table.
 * ------------------------------------------------------------------ */

function kitchenBands(payload: EmailPayload, extra: string[] = []): Block[] {
  const bands: Block[] = []
  const internalPayload = payload.internal
  if (internalPayload?.is_short_notice) bands.push({ kind: 'band', text: 'RÖVID HATÁRIDŐ', tone: 'warn' })
  if (internalPayload?.is_late) bands.push({ kind: 'band', text: 'KÉSŐI RENDELÉS', tone: 'warn' })
  if (internalPayload?.dietary_note) bands.push({ kind: 'band', text: 'ÉTRENDI MEGJEGYZÉS', tone: 'warn' })
  if (internalPayload?.labels_blocked) bands.push({ kind: 'band', text: 'CÍMKÉZÉS BLOKKOLVA', tone: 'stop' })
  for (const text of extra) bands.push({ kind: 'band', text, tone: 'stop' })
  return bands
}

/** §11.6: one row per dish, with allergens as code and full name, and no
 *  price on any kitchen document. The fallback table, for a payload with no
 *  document on it; the document's own table carries the station, the
 *  production units and the production day as well. */
function kitchenDishes(order: OrderPayload): Block {
  return {
    kind: 'table',
    head: ['Fogás', { text: 'Adag', align: 'right' }, 'Gluténmentes', 'Allergének'],
    rows: order.lines.map((line) => [
      { text: line.name_hu ?? line.dish_id },
      { text: qty(line.quantity, line.unit), align: 'right' as const },
      { text: line.is_gf_portion ? 'GLUTÉNMENTES — külön doboz' : line.gluten_free_eligible ? 'alkalmas' : '—' },
      { text: allergenTextLong(line, 'kitchen'), muted: true },
    ]),
  }
}

function kitchenHeader(order: OrderPayload): Block {
  return {
    kind: 'facts',
    rows: keep2([
      ['Rendelés', order.ref],
      ['Verzió', String(order.version)],
      ['Dátum', date(order.event_date, true)],
      order.event_start ? ['Kezdés', time(order.event_start)] : null,
      [
        'Teljesítés',
        order.fulfilment === 'pickup'
          ? `Személyes átvétel${order.slot_label ? ` · ${order.slot_label}` : ''}`
          : `Kiszállítás${order.slot_label ? ` · ${order.slot_label}` : ''}${order.zone ? ` · ${order.zone} zóna` : ''}${km(order.distance_km) === '' ? '' : ` · ${km(order.distance_km)}`}`,
      ],
      ['Vendégek', `${n(order.guests)} fő`],
      order.gf_guests > 0 ? ['Ebből gluténmentes', `${n(order.gf_guests)} fő`] : null,
      /* Run 19 §4: the occasion name and its meal note travel together. */
      order.occasion_label
        ? ['Alkalom', withMealNote(order.occasion_label, mealNoteOf(order, 'hu'))]
        : null,
    ]),
  }
}

/** The order document on the payload, if it is the kind this code prints.
 *  A K3 row carrying a K1 document would be a bug in the digest, not
 *  something to render: the template falls back rather than mislabelling. */
function orderDocument(payload: EmailPayload, kind: string): KitchenOrderDocument | null {
  const document = kitchenDocument(payload)
  if (document === null || document.kind !== kind) return null
  return document as KitchenOrderDocument
}

/** The body every production order shares (§11.6 K1/K2): the bands, the
 *  header, one row per dish, the totals, the dietary note with its resolution
 *  and the delivery block. */
function productionOrderBlocks(doc: KitchenOrderDocument): Block[] {
  return keep([
    ...docBands(doc.bands, doc.superseded, doc.version, doc.current_version),
    docHeader(doc),
    docLines(doc),
    docTotals(doc),
    docAllergenCaveat(doc.lines),
    ...docDietary(doc),
    ...docDelivery(doc),
  ])
}

/** What a kitchen code renders when its document is missing: the order's own
 *  dish table, and one line saying so. Never an empty table. */
function kitchenFallback(payload: EmailPayload, order: OrderPayload): Block[] {
  return keep([
    ...kitchenBands(payload),
    kitchenHeader(order),
    kitchenDishes(order),
    payload.internal?.dietary_note ? { kind: 'heading', text: 'Étrendi megjegyzés' } : null,
    payload.internal?.dietary_note
      ? { kind: 'paragraph', text: payload.internal.dietary_note }
      : null,
    { kind: 'note', text: DOCUMENT_MISSING },
  ])
}

const kitchen: Record<string, Template> = {
  K1: (payload) =>
    withOrder(payload, (order) => {
      const doc = orderDocument(payload, 'K1')
      return {
        subject: `[K1] Gyártási rendelés · ${order.ref} · ${date(order.event_date, true)}`,
        preheader: `${n(order.guests)} vendég · ${date(order.event_date, true)}`,
        blocks: [
          { kind: 'heading', text: 'Gyártási rendelés' },
          ...(doc === null ? kitchenFallback(payload, order) : productionOrderBlocks(doc)),
        ],
      }
    }),

  // §11.6: "K1/K2 production order". The same document; K2 is the one that
  // reaches the kitchen out of sequence, and says which of the two reasons
  // rev7 names applies rather than leaving it to be read off a band.
  K2: (payload) =>
    withOrder(payload, (order) => {
      const doc = orderDocument(payload, 'K2')
      const short = doc?.order.is_short_notice ?? payload.internal?.is_short_notice ?? false
      const late = doc?.order.is_late ?? payload.internal?.is_late ?? false
      // §11.2 and §4 make the two alternatives, and say which wins: a
      // short-notice order is marked RÖVID HATÁRIDŐ "(not KÉSŐI RENDELÉS)",
      // and any other late confirmation is marked KÉSŐI RENDELÉS. A
      // short-notice order is late by construction, so the sentence must not
      // claim both.
      const band = short ? 'RÖVID HATÁRIDŐ' : late ? 'KÉSŐI RENDELÉS' : null
      const why = short
        ? 'Rövid határidős sávban érkezett: a rendezvényig kevesebb idő van a szokásosnál.'
        : late
        ? 'A visszaigazolása a véglegesítési határidő után érkezett.'
        : 'Soron kívüli gyártási rendelés.'
      return {
        subject: `[K2] Gyártási rendelés · ${order.ref} · ${date(order.event_date, true)}`,
        preheader: band ?? 'Soron kívüli gyártási rendelés.',
        blocks: [
          { kind: 'heading', text: 'Gyártási rendelés — soron kívül' },
          { kind: 'paragraph', text: why },
          ...(doc === null ? kitchenFallback(payload, order) : productionOrderBlocks(doc)),
        ],
      }
    }),

  // §11.6: "K3 modification after freeze (MÓDOSÍTVA)". The whole new order,
  // and above it what moved against the version the kitchen is holding.
  K3: (payload) =>
    withOrder(payload, (order) => {
      const doc = orderDocument(payload, 'K3')
      return {
        subject: `[K3] MÓDOSÍTVA · ${order.ref} · ${date(order.event_date, true)}`,
        preheader: 'Véglegesítés után módosult rendelés.',
        blocks: keep([
          { kind: 'heading', text: 'Módosítás véglegesítés után' },
          {
            kind: 'paragraph',
            text:
              'Ez a rendelés a véglegesítés után módosult. A korábbi gyártási rendelés ÉRVÉNYTELEN; ' +
              'ez a változat érvényes.',
          },
          ...(doc === null
            ? [
                { kind: 'band', text: 'MÓDOSÍTVA', tone: 'stop' } as Block,
                ...kitchenFallback(payload, order),
              ]
            : [
                ...docBands(doc.bands),
                docHeader(doc),
                ...(doc.based_on_version === null
                  ? []
                  : [
                      {
                        kind: 'facts',
                        rows: keep2([
                          ['Felváltja', `${doc.based_on_version}. verzió`],
                          doc.version_reason ? ['A módosítás oka', doc.version_reason] : null,
                        ]),
                      } as Block,
                    ]),
                ...docChanges(doc),
                { kind: 'heading', text: 'A teljes gyártási rendelés' } as Block,
                docLines(doc),
                docTotals(doc),
                docAllergenCaveat(doc.lines),
                ...docDietary(doc),
                ...docDelivery(doc),
              ]),
        ]),
      }
    }),

  // §11.6: "K4 cancellation after freeze (TÖRÖLVE)". The lines are on it so
  // the kitchen knows what to stop making, under a band that says not to.
  K4: (payload) =>
    withOrder(payload, (order) => {
      const doc = orderDocument(payload, 'K4')
      return {
        subject: `[K4] TÖRÖLVE · ${order.ref} · ${date(order.event_date, true)}`,
        preheader: 'Véglegesítés után lemondott rendelés.',
        blocks: keep([
          ...(doc === null
            ? [{ kind: 'band', text: 'TÖRÖLVE', tone: 'stop' } as Block]
            : docBands(doc.bands)),
          { kind: 'heading', text: 'Lemondás véglegesítés után' },
          {
            kind: 'paragraph',
            text:
              'Ezt a rendelést ne gyártsák le. A korábbi gyártási rendelés ÉRVÉNYTELEN. ' +
              'Az alábbi tételek maradnak el.',
          },
          ...(doc === null
            ? kitchenFallback(payload, order)
            : keep([
                docHeader(doc),
                docLines(doc),
                docTotals(doc),
                // §7.3: K4 prints the allergen column like every other kitchen
                // document, so it carries the same caveat. A cancellation is
                // not a reason to let example data read as verified.
                docAllergenCaveat(doc.lines),
                ...docDelivery(doc),
              ])),
        ]),
      }
    }),

  // §11.6: "K5 production reminder: everything to make on one production day,
  // grouped by station and dish, split by order."
  K5: (payload) => {
    const document = kitchenDocument(payload)
    const doc = document?.kind === 'K5' ? (document as KitchenProductionDocument) : null
    const day = doc?.for_date ?? payload.for_date ?? payload.created_at
    return {
      subject: `[K5] Gyártási emlékeztető · ${date(day, true)}`,
      // Not "the next business day": K5 is built for whatever production day
      // it is asked for, and the subject already names it.
      preheader: `Ezen a napon gyártandó tételek: ${date(day, true)}`,
      blocks:
        doc === null
          ? [
              ...digestBlocks(payload, 'Az ezen a napon gyártandó tételek rendelésenként.', [
                'Rendelés',
                'Rendezvény dátuma',
                'Vendégek',
                'Adag',
              ]),
              { kind: 'note', text: DOCUMENT_MISSING },
            ]
          : [
              ...docBands(doc.bands),
              { kind: 'paragraph', text: `${date(day, true)} — ezeket kell ezen a napon legyártani.` },
              ...productionBlocks(doc),
            ],
    }
  },

  K6: (payload) => ({
    subject: `[K6] Csomagolási lista és címkék · ${date(payload.for_date ?? payload.created_at, true)}`,
    // Not *a holnapi*: the paragraph below says *a következő munkanapi*, and
    // on a Friday those are different days.
    preheader: 'A következő munkanapi rendezvények csomagolási listája.',
    blocks: [
      ...digestBlocks(payload, 'A következő munkanapi rendezvények csomagolási listája.', [
        'Rendelés',
        'Rendezvény dátuma',
        'Vendégek',
        'Adag',
      ]),
      {
        kind: 'note',
        text:
          'A gluténmentes adagok külön dobozba kerülnek. Címke nem nyomtatható, amíg a címkézés ' +
          'blokkolva van (§11.6). A dobozok, a címkék és a fogyasztási idő a rendelésenkénti K6 ' +
          'nyomtatási nézetében találhatók.',
      },
    ],
  }),

  // §11.6: "K7 forecast and kitchen calendar": the calendar page first, then
  // the dish-level forecast in three columns, changes marked.
  K7: (payload) => {
    const document = kitchenDocument(payload)
    const doc = document?.kind === 'K7' ? (document as KitchenForecastDocument) : null
    const day = doc?.for_date ?? payload.for_date ?? payload.created_at
    return {
      subject: `[K7] Előrejelzés és konyhai naptár · ${date(day, true)}`,
      preheader: doc === null
        ? 'A következő munkanapok előrejelzése.'
        : `A következő ${n(doc.forecast_bd)} munkanap előrejelzése.`,
      blocks:
        doc === null
          ? [
              ...digestBlocks(payload, 'A következő munkanapok rendelései, vendégei és adagjai.', [
                'Rendelés',
                'Rendezvény dátuma',
                'Vendégek',
                'Adag',
              ]),
              { kind: 'note', text: DOCUMENT_MISSING },
            ]
          : forecastBlocks(doc),
    }
  },

  // §11.4, the `dietary_note` row: "Note present; the allergen signatory signs
  // off (K8) **before approval**." The note first, then the dishes it is about.
  K8: (payload) =>
    withOrder(payload, (order) => {
      const doc = orderDocument(payload, 'K8')
      const note = doc?.order.dietary_note ?? payload.internal?.dietary_note ?? null
      return {
        subject: `[K8] Étrendi megjegyzés jóváhagyásra · ${order.ref}`,
        preheader: 'Aláírás szükséges a felülvizsgálat lezárásához.',
        blocks: keep([
          { kind: 'band', text: 'ÉTRENDI MEGJEGYZÉS — aláírás szükséges', tone: 'warn' },
          { kind: 'heading', text: 'A vendég megjegyzése' },
          note === null
            ? { kind: 'note', text: 'HIÁNYZÓ ADAT: a megjegyzés szövege nem olvasható.' }
            : { kind: 'paragraph', text: note },
          ...(doc === null
            ? kitchenFallback(payload, order)
            : keep([
                docHeader(doc),
                docLines(doc),
                docTotals(doc),
                docAllergenCaveat(doc.lines),
                doc.dietary_signoff === null
                  ? null
                  : ({
                      kind: 'facts',
                      rows: [
                        [
                          doc.dietary_signoff.decision === 'signed' ? 'Jóváhagyva' : 'Elutasítva',
                          `${doc.dietary_signoff.resolution} · ${doc.dietary_signoff.signed_by?.name ?? ''}`,
                        ],
                      ],
                    } as Block),
              ])),
          { kind: 'note', text: 'A felülvizsgálat az aláírásig nem zárható le (§11.4).' },
          consoleButton(payload, 'Aláírás megnyitása'),
        ]),
      }
    }),

  K9: (payload) =>
    withOrder(payload, (order) => ({
      subject: `[K9] Katalógusváltozás érinti ezt a rendelést · ${order.ref}`,
      preheader: 'Allergén, gluténmentesség vagy visszavonás változott egy fogáson.',
      blocks: keep([
        { kind: 'band', text: 'CÍMKÉZÉS BLOKKOLVA', tone: 'stop' },
        kitchenHeader(order),
        catalogueChangeTable(payload),
        kitchenDishes(order),
        { kind: 'note', text: 'Címke nem nyomtatható, amíg a riasztást valaki fel nem oldja (§11.9).' },
      ]),
    })),
}

/* ------------------------------------------------------------------ *
 * Small shared blocks the internal templates use
 * ------------------------------------------------------------------ */

/** Internal money: net, VAT, gross and the delivery fee. Internal readers
 *  see both sides whatever the buyer type, which is the one place the
 *  gross-or-net rule of §5.11 does not apply — it is a customer rule. */
function moneySummary(order: OrderPayload): Block {
  const rows: [string, string][] = [
    ['Nettó', ft(order.net)],
    [vatLabel(order.vat_pct), ft(order.vat)],
    ['Bruttó', ft(order.gross)],
  ]
  if (order.delivery_fee > 0) rows.push(['Ebből szállítás (bruttó)', ft(order.delivery_fee)])
  rows.push(['Vevő típusa', order.is_business ? 'cég' : 'magánszemély'])
  return { kind: 'facts', rows }
}

function paymentsTable(payload: EmailPayload): Block {
  if (payload.payments.length === 0) {
    return { kind: 'note', text: 'Nincs rögzített fizetési sor.' }
  }
  return {
    kind: 'table',
    head: ['Típus', 'Szolgáltató', { text: 'Összeg', align: 'right' }, 'Állapot', 'Hivatkozás'],
    rows: payload.payments.map((payment) => [
      { text: PAYMENT_KIND_LABEL[payment.kind] ?? payment.kind },
      { text: payment.provider },
      { text: ft(payment.amount), align: 'right' as const },
      { text: PAYMENT_STATUS_LABEL[payment.status] ?? payment.status },
      { text: payment.provider_txn_id ?? payment.provider_ref ?? '', muted: true },
    ]),
  }
}

function invoicesTable(payload: EmailPayload): Block {
  if (payload.invoices.length === 0) {
    return { kind: 'note', text: 'Nincs rögzített számlasor.' }
  }
  return {
    kind: 'table',
    head: ['Dokumentum', 'Sorszám', { text: 'Bruttó', align: 'right' }, 'Állapot'],
    rows: payload.invoices.map((invoice) => [
      { text: INVOICE_KIND_LABEL[invoice.kind] ?? invoice.kind },
      { text: invoice.szamlazz_number ?? '—' },
      { text: ft(invoice.gross), align: 'right' as const },
      { text: INVOICE_STATUS_LABEL[invoice.status] ?? invoice.status },
    ]),
  }
}

/** Document alert (20261024): which document, its number if it has one, the
 *  Agent's own message, and the buyer email szamlazz.hu sends it to. */
function documentAlertFacts(order: OrderPayload, invoices: InvoicePayload[]): Block {
  return {
    kind: 'facts',
    rows: keep2([
      [
        'Dokumentum',
        invoices.length === 0
          ? '—'
          : invoices
              .map((i) => [INVOICE_KIND_LABEL[i.kind] ?? i.kind, i.szamlazz_number].filter(Boolean).join(' · '))
              .join('; '),
      ],
      ['Vevő e-mail-címe', order.customer_email ?? '—'],
      ...invoices
        .filter((i) => (i.error ?? '').trim() !== '')
        .map((i): [string, string] => ['Hiba', String(i.error)]),
    ]),
  }
}

/** §9.8: the F4 task's own fields. */
function refundTask(payload: EmailPayload, order: OrderPayload): Block {
  const refund = payload.payments.find((payment) => payment.kind === 'refund')
  return {
    kind: 'facts',
    rows: keep2([
      ['Rendelés', order.ref],
      ['Sáv', LANE_LABEL[order.lane] ?? order.lane],
      ['Összeg', ft(refund?.amount ?? order.gross)],
      ['Ok', refund?.note ?? (STATUS_REASON_LABEL[order.status_reason ?? ''] ?? order.status_reason ?? '')],
      refund?.provider_txn_id ? ['Eredeti tranzakció', refund.provider_txn_id] : null,
      refund?.due_at ? ['Határidő', date(refund.due_at)] : null,
    ]),
  }
}

/** §11.9: what changed on the publication against the order's own snapshot.
 *  The rows come from the payload; nothing is compared here. */
function catalogueChangeTable(payload: EmailPayload): Block {
  const changes = payload.digest?.['catalogue_changes']
  if (!Array.isArray(changes) || changes.length === 0) {
    return { kind: 'note', text: 'HIÁNYZÓ ADAT: a változások listája nem érkezett meg az értesítéssel.' }
  }
  return {
    kind: 'table',
    head: ['Fogás', 'Mező', 'Rendelésben', 'Katalógusban'],
    rows: (changes as Record<string, unknown>[]).map((change) => [
      { text: String(change['dish'] ?? '') },
      { text: catalogueFieldLabel(String(change['field'] ?? '')) },
      { text: String(change['was'] ?? '') },
      { text: String(change['now'] ?? '') },
    ]),
  }
}

/** §5.7: C17 carries the basket. The callback is not an order, so the lines
 *  come off the callback row, not off `order_lines`. */
function basketTable(payload: EmailPayload): Block {
  const basket = payload.digest?.['basket']
  if (!Array.isArray(basket) || basket.length === 0) {
    return { kind: 'note', text: 'A kérés nem tartalmaz kosarat.' }
  }
  return {
    kind: 'table',
    head: ['Fogás', { text: 'Mennyiség', align: 'right' }],
    rows: (basket as Record<string, unknown>[]).map((item) => [
      { text: String(item['name'] ?? item['dish_id'] ?? '') },
      { text: qty(Number(item['quantity'] ?? 0), String(item['unit'] ?? '') || null), align: 'right' as const },
    ]),
  }
}

/** The digest codes carry a `rows` array and a few counts; the shape is the
 *  job's, and a digest with nothing in it says so rather than rendering an
 *  empty table. */
/**
 * The Hungarian label for every key a digest can carry, and the shape of its
 * value.
 *
 * `public.outbox_digest_json` builds the digests out of English snake_case
 * keys, as every other payload in this system does: a key is an identifier.
 * The first draft printed them as the fact labels, so C11 reached the kitchen
 * reading *lekötött_adag* and A2 reached admin reading *elutasítás_oka*, and
 * the dates came through in ISO form — which §11.6 forbids on any kitchen
 * output.
 *
 * `date` and `count` say how to write the value, not only what to call it.
 */
const DIGEST_FIELD: Record<string, { label: string; as?: 'date' | 'count' | 'pct' }> = {
  date: { label: 'Nap', as: 'date' },
  event_date: { label: 'Rendezvény dátuma', as: 'date' },
  for_date: { label: 'Nap', as: 'date' },
  committed_portions: { label: 'Lekötött adag', as: 'count' },
  portion_cap: { label: 'Adag korlát', as: 'count' },
  threshold_pct: { label: 'Küszöb', as: 'pct' },
  guests: { label: 'Vendégek', as: 'count' },
  orders: { label: 'Rendelések', as: 'count' },
  failed_code: { label: 'Kód' },
  failed_outbox_id: { label: 'Kimenő sor azonosítója' },
  failed_to: { label: 'Címzett' },
  attempts: { label: 'Próbálkozások', as: 'count' },
  error: { label: 'Hiba' },
  reason: { label: 'Az elutasítás oka' },
  row: { label: 'Érintett sor' },
  kind: { label: 'Mire vonatkozik' },
  published_count: { label: 'Közzétett fogás', as: 'count' },
  /* A3 (DECIDED 20261019): the allergen guard's withdrawal. */
  dish_id: { label: 'Fogás azonosítója' },
  dish_name: { label: 'Fogás' },
  held_at: { label: 'Visszavonva' },
  hold_id: { label: 'Visszavonás azonosítója' },
  sync_run: { label: 'Szinkronfutás' },
  expected: { label: 'Várt összeg' },
  received: { label: 'Beérkezett összeg' },
}

/** A key nobody has labelled is shown as words rather than as source. The
 *  underscore is the thing that must never reach a reader; the English word
 *  behind it is visible enough to be reported and labelled. */
const digestLabel = (key: string): string => {
  const known = DIGEST_FIELD[key]
  if (known) return known.label
  const words = key.replace(/_/g, ' ').trim()
  return words.charAt(0).toUpperCase() + words.slice(1)
}

const digestValue = (key: string, value: string | number): string => {
  const how = DIGEST_FIELD[key]?.as
  if (how === 'date') return date(String(value))
  if (how === 'count' && typeof value === 'number') return n(value)
  if (how === 'pct' && typeof value === 'number') return pct(value)
  // An ISO date under a key nobody declared still must not print as ISO.
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) return date(value)
  return String(value)
}

function digestFacts(payload: EmailPayload): Block {
  const entries = Object.entries(payload.digest ?? {}).filter(
    ([key, value]) => key !== 'rows' && (typeof value === 'string' || typeof value === 'number'),
  )
  if (entries.length === 0) return { kind: 'note', text: 'Nincs adat az értesítésben.' }
  return {
    kind: 'facts',
    rows: entries.map(
      ([key, value]) => [digestLabel(key), digestValue(key, value as string | number)] as [string, string],
    ),
  }
}

/** A figure out of a digest row, grouped as §5.12 writes counts. */
const count = (value: unknown): string => (typeof value === 'number' ? n(value) : String(value ?? ''))

function digestBlocks(payload: EmailPayload, intro: string, head: string[]): Block[] {
  const rows = payload.digest?.['rows']
  const blocks: Block[] = [{ kind: 'paragraph', text: intro }]
  if (!Array.isArray(rows) || rows.length === 0) {
    blocks.push({ kind: 'note', text: 'Nincs ilyen tétel.' })
    return blocks
  }
  blocks.push({
    kind: 'table',
    head,
    rows: (rows as Record<string, unknown>[]).map((row) => [
      { text: String(row['ref'] ?? '') },
      { text: date(String(row['event_date'] ?? '')) },
      // §5.12's grouping, not String(): a five-digit portion total printed
      // 12345 beside a date that was correctly 2026. 10. 16.
      { text: count(row['guests']), align: 'right' as const },
      { text: count(row['portions']), align: 'right' as const },
    ]),
  })
  return blocks
}

/* ------------------------------------------------------------------ *
 * The registry
 * ------------------------------------------------------------------ */

export const TEMPLATES: Record<string, Template> = {
  ...customer,
  ...internal,
  ...finance,
  ...kitchen,
}

/** Every code this build has a template for, in §11.5's own order. */
export const EMAIL_CODES: readonly string[] = [
  'U1', 'U2', 'U3', 'U4', 'U5', 'U6', 'U7', 'U10', 'U11', 'U12',
  'U13', 'U14', 'U15', 'U16', 'U17', 'U18', 'U19', 'U20', 'U21', 'U22',
  'C1', 'C2', 'C3', 'C4', 'C5', 'C7', 'C10', 'C11', 'C12', 'C13',
  'C14', 'C15', 'C16', 'C17',
  'F1', 'F2', 'F4', 'F5', 'F6', 'F7', 'F8', 'F9', 'F10',
  'K1', 'K2', 'K3', 'K4', 'K5', 'K6', 'K7', 'K8', 'K9',
  'A1', 'A2', 'A3', 'A4',
]

/**
 * The codes whose copy is a **draft**, waiting for Barna's and Anikó's review.
 *
 * `build/FIXES.md` item 79 (F8) makes its two new customer emails *the one
 * exception to the copy rule* — the build writes them rather than reusing a
 * string the brief gives — so they are listed here, the preview page marks
 * them, and `public.notification_codes.description` says DRAFT too. Nothing
 * a customer receives carries the word.
 */
export const DRAFT_CODES: readonly string[] = ['U21', 'U22']

/**
 * The fallback for a code with no template. It is not an error path that can
 * be reached today — `test/templates.test.ts` checks every code of §11.5 has
 * one — but a code added to the database and not here must still produce a
 * message somebody reads, rather than a send that fails five times.
 */
export const fallbackTemplate: Template = (payload) => ({
  subject: `[${payload.code}] értesítés`,
  preheader: 'Ehhez a kódhoz még nincs sablon.',
  blocks: keep([
    {
      kind: 'band',
      text: `HIÁNYZÓ SABLON: a(z) ${payload.code} kódhoz nincs e-mail sablon. §11.5 és §19.5.`,
      tone: 'stop',
    },
    payload.order ? internalHeader(payload, payload.order) : null,
  ]),
})
